import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, ScrollText, Tag, CheckCircle, Clock, XCircle, Search, Pencil,
  Trash2, Plus, Printer, BookOpen, BarChart3, Lightbulb, Gift, Store, Coins, CircleDot,
  FileSpreadsheet, Download, Upload, UserPlus, AlertTriangle, ShoppingCart
} from 'lucide-react';
import * as XLSX from 'xlsx';
import LogModal from '../components/LogModal';
import logService from '../services/logService';
import { API_URL, API_HOST } from '../config/api';
import useEscapeClose from '../hooks/useEscapeClose';
import { handlePrint } from '../utils/printHelper';
import PriceInput from '../components/PriceInput';
import './Campaigns.css';

const CAMPAIGN_TYPES = {
  percentage: { label: 'Yüzde İndirim', description: 'Toplam tutardan yüzde indirim' },
  fixed_amount: { label: 'Tutar İndirim', description: 'Sabit tutar indirim' },
  progressive: { label: 'Kademeli İndirim', description: '1. ürün %10, 2. ürün %20 gibi' },
  buy_x_pay_y: { label: 'X Al Y Öde', description: '3 al 2 öde gibi kampanyalar' },
  bundle: { label: 'Set Kampanyası', description: 'Belirli ürünler birlikte alındığında' },
  gift_with_purchase: { label: 'Hediye Kampanyası', description: 'Belirli alışverişlerde hediye' },
  category_discount: { label: 'Kategori İndirimi', description: 'Kategoriye özel indirim' },
  product_discount: { label: 'Ürün İndirimi', description: 'Belirli ürünlerde indirim' },
  special_day: { label: 'Özel Gün Kampanyası', description: 'Belirli tarih/aralıkta indirim' },
  birthday: { label: 'Doğum Günü Kampanyası', description: 'Doğum günü olan müşterilere indirim' }
};

const CATEGORIES = []; // Dinamik olarak DB'den yükleniyor

// Tarih formatı için helper - timezone sorununu önler
const formatDateTR = (dateStr) => {
  if (!dateStr) return '';
  // YYYY-MM-DD formatını al (T'den önceki kısmı)
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-');
  return `${day}.${month}.${year}`;
};

const Campaigns = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [storeTypeFilter, setStoreTypeFilter] = useState('all'); // Admin için mağaza tipi filtresi
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [originalCampaign, setOriginalCampaign] = useState(null);
  const [stores, setStores] = useState([]);
  const [dbCategories, setDbCategories] = useState({}); // { gold: ['Kolye','Yüzük',...], silver: [...], ... }
  const [dbBrands, setDbBrands] = useState({}); // { gold: ['Akik Jewellery'], silver: ['Akik Gümüş','Pandora',...], ... }

  // Kullanıcının mağaza tipini belirle
  const getUserStoreType = () => {
    if (user?.role === 'admin') return null; // Admin tüm mağazaları görebilir
    const storeId = user?.store_id || selectedStore?.id;
    if (storeId === 1) return 'gold';
    if (storeId === 2 || storeId === 3) return 'silver';
    return null;
  };
  const userStoreType = getUserStoreType();

  // Yetki kontrolü - Admin her zaman yetkili, kasiyer ise izne bağlı
  const canEditCampaigns = () => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'cashier') return user?.permissions?.canEditCampaigns === true;
    return false;
  };

  // Kampanya kullanım geçmişi için state'ler
  const [showUsageModal, setShowUsageModal] = useState(null); // Seçilen kampanya
  const [campaignUsageHistory, setCampaignUsageHistory] = useState([]);
  const [campaignUsageStats, setCampaignUsageStats] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [showUsageDetail, setShowUsageDetail] = useState(null);

  // Hediye kartları için state'ler
  const [activeTab, setActiveTab] = useState('campaigns'); // 'campaigns', 'giftcards' veya 'scrapDebts'
  const [giftCards, setGiftCards] = useState([]);
  const [scrapDebtCards, setScrapDebtCards] = useState([]); // Hurda borçları
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [giftCardForm, setGiftCardForm] = useState({
    code: '',
    amount: '',
    expires_at: '',
    card_type: 'random', // 'random' veya 'customer_specific'
    customer_id: null,
    customer_name: '',
    currency: 'TL' // 'TL', 'USD', 'EUR'
  });

  // Hediye kartı düzenleme state'leri
  const [showEditGiftCardModal, setShowEditGiftCardModal] = useState(false);
  const [editingGiftCard, setEditingGiftCard] = useState(null);
  const [editGiftCardForm, setEditGiftCardForm] = useState({
    amount: '',
    remaining_amount: '',
    expires_at: '',
    customer_id: null,
    customer_name: '',
    currency: 'TL'
  });

  // Hediye kartı müşteri arama state'leri
  const [giftCardCustomerSearch, setGiftCardCustomerSearch] = useState('');
  const [giftCardCustomerResults, setGiftCardCustomerResults] = useState([]);
  const [showGiftCardCustomerDropdown, setShowGiftCardCustomerDropdown] = useState(false);

  // Hurda borcu düzenleme state'leri
  const [showEditScrapDebtModal, setShowEditScrapDebtModal] = useState(false);
  const [editingScrapDebt, setEditingScrapDebt] = useState(null);
  const [editScrapDebtForm, setEditScrapDebtForm] = useState({
    amount: '',
    remaining_amount: '',
    debt_type: 'TL',
    customer_name: ''
  });

  // Excel import state'leri
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelImportData, setExcelImportData] = useState([]);
  const [excelImportStep, setExcelImportStep] = useState('select'); // 'select', 'preview', 'confirm_new_customers'
  const [unmatchedCustomers, setUnmatchedCustomers] = useState([]);
  const [matchedCustomers, setMatchedCustomers] = useState([]);
  const [excelProcessing, setExcelProcessing] = useState(false);

  // Log Modal
  const [showLogModal, setShowLogModal] = useState(false);

  // Yardım Modal
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Modal kapanış animasyonu için
  const [closingModal, setClosingModal] = useState(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showHelpModal, () => setShowHelpModal(false)],
    [showUsageDetail, () => setShowUsageDetail(null)],
    [showEditGiftCardModal, () => setShowEditGiftCardModal(false)],
    [showEditScrapDebtModal, () => setShowEditScrapDebtModal(false)],
    [showUsageModal, () => setShowUsageModal(null)],
    [showModal, () => setShowModal(false)],
    [showGiftCardModal, () => setShowGiftCardModal(false)],
    [showExcelModal, () => setShowExcelModal(false)],
    [showLogModal, () => {}],
  ]);

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Hediye ürün seçimi için state'ler
  const [giftProductSearch, setGiftProductSearch] = useState('');
  const [giftProductCategory, setGiftProductCategory] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    campaign_type: 'percentage',
    store_type: 'all', // 'all', 'gold', 'silver'
    conditions: {
      min_purchase: 0,
      min_purchase_type: 'category', // 'general' veya 'category'
      categories: [],
      products: [],
      buy_quantity: 3,
      required_products: [],
      special_dates: [], // Özel gün kampanyası için tarihler
      birthday_days_before: 0, // Doğum gününden kaç gün önce
      birthday_days_after: 0 // Doğum gününden kaç gün sonra
    },
    rewards: {
      discount_percent: 10,
      discount_amount: 0,
      discount_type: 'percentage', // 'percentage' veya 'fixed'
      free_quantity: 1,
      gift_product_id: null,
      gift_product_name: '',
      tiers: [
        { item_number: 1, discount_percent: 10 },
        { item_number: 2, discount_percent: 20 },
        { item_number: 3, discount_percent: 30 }
      ],
      repeat: false // Kademeli indirimleri tekrarla
    },
    start_date: '',
    end_date: '',
    priority: 0,
    usage_limit: null,
    active: true,
    ecommerce_active: false
  });

  // Mağaza tipi isimleri
  const getStoreTypeName = (type) => {
    if (type === 'all') return 'Tüm Mağazalar';
    if (type === 'gold') return 'Altın Mağazaları';
    if (type === 'silver') return 'Gümüş Mağazaları';
    if (type?.startsWith('store_')) {
      const storeId = parseInt(type.replace('store_', ''));
      const store = stores.find(s => s.id === storeId);
      return store ? store.name : type;
    }
    return type || 'Tüm Mağazalar';
  };
  const STORE_TYPE_NAMES = {
    all: 'Tüm Mağazalar',
    gold: 'Altın Mağazaları',
    silver: 'Gümüş Mağazaları'
  };

  // Seçilen store_type'a göre kategorileri getir
  const getCategoriesForStoreType = () => {
    const st = formData.store_type;
    if (!st || st === 'all') {
      // Tüm kategoriler
      const all = new Set();
      Object.values(dbCategories).forEach(cats => cats.forEach(c => all.add(c)));
      return [...all].sort();
    }
    if (st === 'gold') return dbCategories.gold || [];
    if (st === 'silver') {
      // Gümüş mağazaları: gold hariç tüm türlerin kategorileri
      const all = new Set();
      Object.entries(dbCategories).forEach(([mt, cats]) => {
        if (mt !== 'gold' && mt !== 'all') cats.forEach(c => all.add(c));
      });
      return [...all].sort();
    }
    if (st.startsWith('store_')) {
      const storeId = parseInt(st.replace('store_', ''));
      const store = stores.find(s => s.id === storeId);
      if (store?.metal_type === 'gold') return dbCategories.gold || [];
      // Gümüş mağazası: gold hariç hepsi
      const all = new Set();
      Object.entries(dbCategories).forEach(([mt, cats]) => {
        if (mt !== 'gold' && mt !== 'all') cats.forEach(c => all.add(c));
      });
      return [...all].sort();
    }
    return [];
  };

  useEffect(() => {
    fetchCampaigns();
    fetchProducts();
    fetchGiftCards();
    // Mağazaları ve kategorileri çek
    (async () => {
      try {
        const [storeRes, catRes, brandRes] = await Promise.all([
          fetch(`${API_URL}/stores`),
          fetch(`${API_URL}/products/counts-by-type`),
          fetch(`${API_URL}/products/brands-by-type`)
        ]);
        const storeData = await storeRes.json();
        if (Array.isArray(storeData)) setStores(storeData);
        const catData = await catRes.json();
        if (catData.categories) {
          const grouped = {};
          Object.keys(catData.categories).forEach(key => {
            const [metalType, cat] = key.split('|');
            if (!cat) return;
            if (!grouped[metalType]) grouped[metalType] = [];
            if (!grouped[metalType].includes(cat)) grouped[metalType].push(cat);
          });
          Object.keys(grouped).forEach(mt => grouped[mt].sort());
          setDbCategories(grouped);
        }
        const brandData = await brandRes.json();
        if (brandData.brands) setDbBrands(brandData.brands);
      } catch (e) { console.error('Mağaza/kategori/marka yükleme hatası:', e); }
    })();
  }, []);

  // Mağaza filtresi değiştiğinde kampanyaları yeniden yükle
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchCampaigns();
    }
  }, [storeTypeFilter]);

  // Modal kapanış handler'ı (callback opsiyonel)
  const handleCloseModal = (modalName, setShowModal, callback) => {
    setClosingModal(modalName);
    setTimeout(() => {
      setShowModal(false);
      setClosingModal(null);
      if (callback) callback();
    }, 250);
  };

  // Hediye kartlarını getir
  const fetchGiftCards = async () => {
    try {
      const response = await fetch(`${API_URL}/gift-cards`);
      const data = await response.json();
      const allCards = Array.isArray(data) ? data : [];

      // Normal bakiyeler (source != 'scrap_purchase')
      const normalCards = allCards.filter(c => c.source !== 'scrap_purchase');
      setGiftCards(normalCards);

      // Hurda borcu kartları (source == 'scrap_purchase')
      const scrapCards = allCards.filter(c => c.source === 'scrap_purchase');
      setScrapDebtCards(scrapCards);
    } catch (error) {
      console.error('Hediye kartları yüklenemedi:', error);
      setGiftCards([]);
      setScrapDebtCards([]);
    }
  };

  // Hediye kartı için müşteri ara
  const searchGiftCardCustomer = async (query) => {
    setGiftCardCustomerSearch(query);
    if (!query || query.length < 2) {
      setGiftCardCustomerResults([]);
      setShowGiftCardCustomerDropdown(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/customers/search/${query}`);
      const data = await response.json();
      setGiftCardCustomerResults(data.customers || data || []);
      setShowGiftCardCustomerDropdown(true);
    } catch (error) {
      console.error('Müşteri arama hatası:', error);
      setGiftCardCustomerResults([]);
    }
  };

  // Hediye kartı için müşteri seç
  const selectGiftCardCustomer = (customer) => {
    setGiftCardForm({
      ...giftCardForm,
      customer_id: customer.id,
      customer_name: customer.name
    });
    setGiftCardCustomerSearch('');
    setGiftCardCustomerResults([]);
    setShowGiftCardCustomerDropdown(false);
  };

  // Hediye kartı müşteri seçimini temizle
  const clearGiftCardCustomer = () => {
    setGiftCardForm({
      ...giftCardForm,
      customer_id: null,
      customer_name: ''
    });
  };

  // ===== EXCEL IMPORT FONKSİYONLARI =====

  // Örnek Excel şablonu indir
  const downloadExcelTemplate = () => {
    const templateData = [
      ['Müşteri Ad Soyad', 'Telefon No', 'Miktar', 'Para Birimi'],
      ['Ahmet Yılmaz', '05551234567', '100', 'TL'],
      ['Mehmet Kaya', '05559876543', '50', 'USD'],
      ['Ayşe Demir', '05557654321', '75', 'EUR']
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);

    // Sütun genişliklerini ayarla
    ws['!cols'] = [
      { wch: 25 }, // Müşteri Ad Soyad
      { wch: 15 }, // Telefon No
      { wch: 12 }, // Miktar
      { wch: 12 }  // Para Birimi
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hediye Kartları');
    XLSX.writeFile(wb, 'hediye_karti_sablonu.xlsx');
    toast.success('Şablon dosyası indirildi!');
  };

  // Excel dosyası okuma
  const handleExcelFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExcelProcessing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // İlk satır başlık, kalanlar veri
      const rows = jsonData.slice(1).filter(row => row.length >= 3 && row[0] && row[2]);

      if (rows.length === 0) {
        toast.warning('Excel dosyasında geçerli veri bulunamadı!');
        setExcelProcessing(false);
        return;
      }

      // Tüm müşterileri getir
      const customersResponse = await fetch(`${API_URL}/customers?limit=10000`);
      const customersData = await customersResponse.json();
      const allCustomers = customersData.customers || customersData || [];

      const matched = [];
      const unmatched = [];

      rows.forEach((row, index) => {
        const name = String(row[0] || '').trim();
        const phone = String(row[1] || '').trim().replace(/\D/g, ''); // Sadece rakamlar
        const amount = parseFloat(row[2]) || 0;
        const currency = String(row[3] || 'TL').toUpperCase().trim();

        // Geçerli para birimi kontrolü
        const validCurrency = ['TL', 'USD', 'EUR'].includes(currency) ? currency : 'TL';

        // Müşteri eşleştirme (isim veya telefon ile)
        const matchedCustomer = allCustomers.find(c => {
          const customerName = (c.name || '').toLowerCase().trim();
          const customerPhone = (c.phone || '').replace(/\D/g, '');
          const searchName = name.toLowerCase();
          const searchPhone = phone;

          // Telefon ile tam eşleşme veya isim ile yakın eşleşme
          if (searchPhone && customerPhone && customerPhone.includes(searchPhone)) return true;
          if (customerName === searchName) return true;
          // İsimde kısmi eşleşme (en az 3 karakter)
          if (searchName.length >= 3 && customerName.includes(searchName)) return true;
          return false;
        });

        const rowData = {
          index: index + 2, // Excel satır numarası (başlık dahil)
          name,
          phone,
          amount,
          currency: validCurrency,
          originalCurrency: currency
        };

        if (matchedCustomer) {
          matched.push({
            ...rowData,
            customer: matchedCustomer
          });
        } else {
          unmatched.push(rowData);
        }
      });

      setMatchedCustomers(matched);
      setUnmatchedCustomers(unmatched);
      setExcelImportStep(unmatched.length > 0 ? 'confirm_new_customers' : 'preview');
      setExcelProcessing(false);

    } catch (error) {
      console.error('Excel okuma hatası:', error);
      toast.error('Excel dosyası okunamadı!');
      setExcelProcessing(false);
    }

    // Input'u sıfırla
    e.target.value = '';
  };

  // Yeni müşteri oluştur ve bakiye kaydını ekle
  const createNewCustomerAndGiftCard = async (customerData) => {
    try {
      // Yeni müşteri oluştur
      const customerResponse = await fetch(`${API_URL}/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customerData.name,
          phone: customerData.phone || null
        })
      });

      const newCustomer = await customerResponse.json();
      if (newCustomer.id || newCustomer.customer?.id) {
        const customerId = newCustomer.id || newCustomer.customer?.id;
        const customerName = newCustomer.name || newCustomer.customer?.name || customerData.name;

        // Hediye kartı oluştur
        await createGiftCardFromExcel({
          ...customerData,
          customer: { id: customerId, name: customerName }
        });

        return true;
      }
      return false;
    } catch (error) {
      console.error('Müşteri oluşturma hatası:', error);
      return false;
    }
  };

  // Excel'den hediye kartı oluştur
  const createGiftCardFromExcel = async (data) => {
    try {
      const response = await fetch(`${API_URL}/gift-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: data.amount,
          customer_id: data.customer?.id,
          customer_name: data.customer?.name,
          amount_unit: data.currency,
          created_by: 'Excel Import'
        })
      });

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Hediye kartı oluşturma hatası:', error);
      return false;
    }
  };

  // Eşleşen müşterilerin hediye kartlarını oluştur
  const processMatchedCustomers = async () => {
    setExcelProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const item of matchedCustomers) {
      const success = await createGiftCardFromExcel(item);
      if (success) successCount++;
      else errorCount++;
    }

    setExcelProcessing(false);
    return { successCount, errorCount };
  };

  // Tüm import'u tamamla (yeni müşteriler dahil)
  const completeExcelImport = async (createNewCustomers = false) => {
    setExcelProcessing(true);
    let successCount = 0;
    let errorCount = 0;
    let newCustomerCount = 0;

    // Eşleşen müşteriler
    for (const item of matchedCustomers) {
      const success = await createGiftCardFromExcel(item);
      if (success) successCount++;
      else errorCount++;
    }

    // Yeni müşteriler (eğer onaylandıysa)
    if (createNewCustomers) {
      for (const item of unmatchedCustomers) {
        const success = await createNewCustomerAndGiftCard(item);
        if (success) {
          successCount++;
          newCustomerCount++;
        } else {
          errorCount++;
        }
      }
    }

    setExcelProcessing(false);
    fetchGiftCards();

    // Modal'ı kapat ve state'leri sıfırla
    closeExcelModal();

    // Sonuç bildirimi
    const messages = [];
    if (successCount > 0) messages.push(`${successCount} bakiye oluşturuldu`);
    if (newCustomerCount > 0) messages.push(`${newCustomerCount} yeni müşteri kaydedildi`);
    if (errorCount > 0) messages.push(`${errorCount} hata oluştu`);

    if (successCount > 0) {
      toast.success(messages.join(', '));
    } else {
      toast.error('Hiçbir bakiye oluşturulamadı!');
    }

    // Log kaydı
    logService.log('campaigns', 'import',
      `Excel'den ${successCount} bakiye içe aktarıldı`, {
      entity_type: 'gift_card',
      details: {
        toplam_kart: successCount,
        yeni_musteri: newCustomerCount,
        hata: errorCount
      },
      status: errorCount === 0 ? 'success' : 'warning'
    });
  };

  // Excel modal'ını kapat ve sıfırla
  const closeExcelModal = () => {
    setShowExcelModal(false);
    setExcelImportStep('select');
    setMatchedCustomers([]);
    setUnmatchedCustomers([]);
    setExcelImportData([]);
  };

  // ===== EXCEL IMPORT FONKSİYONLARI SONU =====

  // Hediye kartı oluştur
  const createGiftCard = async () => {
    if (!giftCardForm.amount || giftCardForm.amount <= 0) {
      toast.warning('Lütfen geçerli bir tutar girin!');
      return;
    }

    // Müşteriye özel kart seçildiyse müşteri kontrolü
    if (giftCardForm.card_type === 'customer_specific' && !giftCardForm.customer_id) {
      toast.warning('Müşteriye özel kart için bir müşteri seçmelisiniz!');
      return;
    }

    // Para birimi sembolleri
    const currencySymbols = { TL: '₺', USD: '$', EUR: '€' };
    const currencySymbol = currencySymbols[giftCardForm.currency] || '₺';

    try {
      const response = await fetch(`${API_URL}/gift-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: giftCardForm.code || null,
          amount: parseFloat(giftCardForm.amount),
          expires_at: giftCardForm.expires_at || null,
          created_by: 'Admin',
          customer_id: giftCardForm.card_type === 'customer_specific' ? giftCardForm.customer_id : null,
          customer_name: giftCardForm.card_type === 'customer_specific' ? giftCardForm.customer_name : null,
          amount_unit: giftCardForm.currency || 'TL'
        })
      });

      const data = await response.json();
      if (data.success) {
        const sonKullanmaTarihi = giftCardForm.expires_at ? formatDateTR(giftCardForm.expires_at) : 'Süresiz';
        const musteriInfo = giftCardForm.card_type === 'customer_specific' ? ` - Müşteri: ${giftCardForm.customer_name}` : '';
        logService.log('campaigns', 'create',
          `Hediye kartı oluşturuldu: ${data.gift_card.code} - ${currencySymbol}${data.gift_card.amount}${musteriInfo}`, {
          entity_type: 'gift_card',
          entity_id: data.gift_card.id,
          details: {
            kart_kodu: data.gift_card.code,
            tutar: `${currencySymbol}${Number(data.gift_card.amount).toLocaleString()}`,
            para_birimi: giftCardForm.currency,
            son_kullanma: sonKullanmaTarihi,
            olusturan: 'Admin',
            kart_tipi: giftCardForm.card_type === 'customer_specific' ? 'Müşteriye Özel' : 'Rastgele',
            musteri: giftCardForm.customer_name || '-'
          },
          status: 'success'
        });
        const successMsg = giftCardForm.card_type === 'customer_specific'
          ? `Hediye kartı oluşturuldu! Kod: ${data.gift_card.code} - Tutar: ${currencySymbol}${data.gift_card.amount} - Müşteri: ${giftCardForm.customer_name}`
          : `Hediye kartı oluşturuldu! Kod: ${data.gift_card.code} - Tutar: ${currencySymbol}${data.gift_card.amount}`;
        toast.success(successMsg);
        setShowGiftCardModal(false);
        setGiftCardForm({ code: '', amount: '', expires_at: '', card_type: 'random', customer_id: null, customer_name: '', currency: 'TL' });
        setGiftCardCustomerSearch('');
        setGiftCardCustomerResults([]);
        fetchGiftCards();
      } else {
        toast.error(data.error || 'Hediye kartı oluşturulamadı!');
        logService.log('campaigns', 'create', `Hediye kartı oluşturulamadı`, {
          entity_type: 'gift_card',
          details: { tutar: giftCardForm.amount, hata: data.error },
          status: 'error'
        });
      }
    } catch (error) {
      console.error('Hediye kartı oluşturma hatası:', error);
      toast.error('Hediye kartı oluşturulamadı!');
      logService.log('campaigns', 'create', `Hediye kartı hatası`, {
        entity_type: 'gift_card',
        details: { tutar: giftCardForm.amount, hata: error.message },
        status: 'error'
      });
    }
  };

  // Hediye kartı düzenleme modalını aç
  const openEditGiftCardModal = (card) => {
    setEditingGiftCard(card);
    setEditGiftCardForm({
      amount: card.amount || '',
      remaining_amount: card.remaining_amount || '',
      expires_at: card.expires_at ? card.expires_at.split('T')[0] : '',
      customer_id: card.customer_id || null,
      customer_name: card.customer_name || '',
      currency: card.amount_unit || 'TL'
    });
    setShowEditGiftCardModal(true);
  };

  // Hediye kartı güncelle
  const updateGiftCard = async () => {
    if (!editingGiftCard) return;

    try {
      const response = await fetch(`${API_URL}/gift-cards/${editingGiftCard.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(editGiftCardForm.amount),
          remaining_amount: parseFloat(editGiftCardForm.remaining_amount),
          expires_at: editGiftCardForm.expires_at || null,
          customer_id: editGiftCardForm.customer_id,
          customer_name: editGiftCardForm.customer_name,
          amount_unit: editGiftCardForm.currency
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Hediye kartı güncellendi');
        fetchGiftCards();
        setShowEditGiftCardModal(false);
        setEditingGiftCard(null);
        logService.log('campaigns', 'update',
          `${editingGiftCard.code} kodlu hediye kartı güncellendi`,
          { card_id: editingGiftCard.id }
        );
      } else {
        toast.error(data.error || 'Güncelleme hatası');
      }
    } catch (error) {
      console.error('Hediye kartı güncelleme hatası:', error);
      toast.error('Güncelleme hatası: ' + error.message);
    }
  };

  // Hurda borcu düzenleme modalını aç
  const openEditScrapDebtModal = (card) => {
    setEditingScrapDebt(card);
    setEditScrapDebtForm({
      amount: card.amount || '',
      remaining_amount: card.remaining_amount || '',
      debt_type: card.debt_type || 'TL',
      customer_name: card.customer_name || ''
    });
    setShowEditScrapDebtModal(true);
  };

  // Hurda borcu güncelle
  const updateScrapDebt = async () => {
    if (!editingScrapDebt) return;

    try {
      const response = await fetch(`${API_URL}/gift-cards/${editingScrapDebt.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(editScrapDebtForm.amount),
          remaining_amount: parseFloat(editScrapDebtForm.remaining_amount),
          debt_type: editScrapDebtForm.debt_type,
          customer_name: editScrapDebtForm.customer_name
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Hurda borcu güncellendi');
        fetchGiftCards();
        setShowEditScrapDebtModal(false);
        setEditingScrapDebt(null);
        logService.log('campaigns', 'update',
          `${editingScrapDebt.code} kodlu hurda borcu güncellendi`,
          { card_id: editingScrapDebt.id }
        );
      } else {
        toast.error(data.error || 'Güncelleme hatası');
      }
    } catch (error) {
      console.error('Hurda borcu güncelleme hatası:', error);
      toast.error('Güncelleme hatası: ' + error.message);
    }
  };

  // Hediye kartı iptal et
  const cancelGiftCard = async (card) => {
    showConfirmation({
      title: 'Hediye Kartı İptal',
      message: 'Bu hediye kartını iptal etmek istediğinize emin misiniz?',
      confirmText: 'Evet, İptal Et',
      cancelText: 'Vazgeç',
      type: 'warning',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/gift-cards/${card.id}/cancel`, { method: 'PUT' });
          const data = await response.json();
          if (data.success) {
            logService.log('campaigns', 'update',
              `Hediye kartı iptal edildi: ${card.code}`, {
              entity_type: 'gift_card',
              entity_id: card.id,
              details: {
                kart_kodu: card.code,
                tutar: `₺${Number(card.amount).toLocaleString()}`,
                kalan_bakiye: `₺${Number(card.remaining_amount || card.amount).toLocaleString()}`,
                kullanilan: `₺${Number((card.amount || 0) - (card.remaining_amount || card.amount)).toLocaleString()}`,
                islem: 'İptal Edildi'
              },
              status: 'success'
            });
            toast.success('Hediye kartı iptal edildi!');
            fetchGiftCards();
          }
        } catch (error) {
          console.error('Hediye kartı iptal hatası:', error);
          logService.log('campaigns', 'update', `Hediye kartı iptal hatası: ${card.code}`, {
            entity_type: 'gift_card',
            entity_id: card.id,
            details: { kart_kodu: card.code, hata: error.message },
            status: 'error'
          });
        }
      }
    });
  };

  // Hediye kartı sil
  const deleteGiftCard = async (card) => {
    showConfirmation({
      title: 'Hediye Kartı Sil',
      message: 'Bu hediye kartını silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/gift-cards/${card.id}`, { method: 'DELETE' });
          const data = await response.json();
          if (data.success) {
            logService.log('campaigns', 'delete',
              `Hediye kartı silindi: ${card.code} - ₺${card.amount}`, {
              entity_type: 'gift_card',
              entity_id: card.id,
              details: {
                kart_kodu: card.code,
                tutar: `₺${Number(card.amount).toLocaleString()}`,
                durum: card.status === 'active' ? 'Aktif' : card.status === 'used' ? 'Kullanıldı' : 'İptal',
                olusturma_tarihi: formatDateTR(card.created_at)
              },
              status: 'success'
            });
            toast.success('Hediye kartı silindi!');
            fetchGiftCards();
          }
        } catch (error) {
          console.error('Hediye kartı silme hatası:', error);
          logService.log('campaigns', 'delete', `Hediye kartı silme hatası: ${card.code}`, {
            entity_type: 'gift_card',
            entity_id: card.id,
            details: { kart_kodu: card.code, hata: error.message },
            status: 'error'
          });
        }
      }
    });
  };

  // Hediye kartı fişi yazdır
  const printGiftCard = (card) => {
    const expiryDate = card.expires_at
      ? new Date(card.expires_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
      : 'Süresiz';

    const createdDate = new Date(card.created_at).toLocaleDateString('tr-TR', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });

    const giftCardHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Hediye Kartı - ${card.code}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Segoe UI', Arial, sans-serif;
            padding: 20px;
            background: #fff;
          }
          .receipt {
            max-width: 350px;
            margin: 0 auto;
            border: 2px dashed #ccc;
            padding: 25px;
            border-radius: 12px;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #f0f0f0;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .logo {
            font-size: 24px;
            font-weight: bold;
            color: #00bfa6;
            margin-bottom: 5px;
          }
          .subtitle {
            font-size: 12px;
            color: #888;
          }
          .gift-icon {
            font-size: 48px;
            margin: 15px 0;
          }
          .code-section {
            background: linear-gradient(135deg, #00bfa6 0%, #009688 100%);
            color: white;
            padding: 20px;
            border-radius: 10px;
            text-align: center;
            margin: 20px 0;
          }
          .code-label {
            font-size: 11px;
            opacity: 0.9;
            margin-bottom: 5px;
          }
          .code {
            font-size: 28px;
            font-weight: bold;
            letter-spacing: 3px;
            font-family: Arial, Helvetica, sans-serif;
          }
          .amount-section {
            text-align: center;
            margin: 25px 0;
          }
          .amount-label {
            font-size: 12px;
            color: #888;
          }
          .amount {
            font-size: 42px;
            font-weight: bold;
            color: #2d3748;
          }
          .amount span {
            font-size: 20px;
            color: #888;
          }
          .details {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            margin: 15px 0;
          }
          .detail-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            font-size: 13px;
            border-bottom: 1px solid #eee;
          }
          .detail-row:last-child {
            border-bottom: none;
          }
          .detail-label {
            color: #666;
          }
          .detail-value {
            font-weight: 600;
            color: #333;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px dashed #ccc;
          }
          .footer p {
            font-size: 11px;
            color: #888;
            margin: 5px 0;
          }
          .barcode {
            font-family: 'Libre Barcode 39', 'Courier New', monospace;
            font-size: 36px;
            letter-spacing: -2px;
            margin: 10px 0;
          }
          @media print {
            body { padding: 0; }
            .receipt { border: none; }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <div class="logo">Akik Gümüş & Jewellery</div>
            <div class="subtitle">HEDİYE KARTI</div>
            <div class="gift-icon">🎁</div>
          </div>

          <div class="code-section">
            <div class="code-label">HEDİYE KODU</div>
            <div class="code">${card.code}</div>
          </div>

          <div class="amount-section">
            <div class="amount-label">KART BAKİYESİ</div>
            <div class="amount"><span>₺</span>${Number(card.amount).toLocaleString('tr-TR')}</div>
          </div>

          <div class="details">
            <div class="detail-row">
              <span class="detail-label">Oluşturma Tarihi:</span>
              <span class="detail-value">${createdDate}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Son Kullanma Tarihi:</span>
              <span class="detail-value">${expiryDate}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Durum:</span>
              <span class="detail-value">${card.status === 'active' ? 'Aktif' : card.status === 'used' ? 'Kullanıldı' : 'İptal'}</span>
            </div>
          </div>

          <div class="footer">
            <div class="barcode">*${card.code}*</div>
            <p>Bu kart tüm Akik mağazalarında geçerlidir.</p>
            <p>Nakit iade yapılmaz.</p>
          </div>
        </div>

        <script>
          window.onload = function() {
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            window.print();
            if (directPrint) { setTimeout(function() { window.close(); }, 500); }
          }
        <\/script>
      </body>
      </html>
    `;
    handlePrint(giftCardHtml, 'receipt');
  };

  // Belirli bir kampanyanın kullanım geçmişini getir
  const fetchCampaignUsage = async (campaign) => {
    try {
      setUsageLoading(true);
      setShowUsageModal(campaign);

      // Kullanım geçmişi ve istatistikleri paralel olarak getir
      const [usageRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/campaigns/${campaign.id}/usage`),
        fetch(`${API_URL}/campaigns/${campaign.id}/stats`)
      ]);

      const usageData = await usageRes.json();
      const statsData = await statsRes.json();

      setCampaignUsageHistory(Array.isArray(usageData) ? usageData : []);
      setCampaignUsageStats(statsData);
    } catch (error) {
      console.error('Kampanya kullanım geçmişi yükleme hatası:', error);
      setCampaignUsageHistory([]);
      setCampaignUsageStats(null);
    } finally {
      setUsageLoading(false);
    }
  };

  const closeUsageModal = () => {
    setClosingModal('usage');
    setTimeout(() => {
      setShowUsageModal(null);
      setCampaignUsageHistory([]);
      setCampaignUsageStats(null);
      setClosingModal(null);
    }, 250);
  };

  const getUsageProducts = (usage) => {
    try {
      const products = typeof usage.products === 'string'
        ? JSON.parse(usage.products)
        : usage.products || [];
      return products;
    } catch (e) {
      return [];
    }
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      // Mağaza tipine göre filtrele
      let storeTypeParam = '';
      if (user?.role === 'admin') {
        // Admin için seçilen filtreyi kullan
        if (storeTypeFilter && storeTypeFilter !== 'all') {
          storeTypeParam = `?store_type=${storeTypeFilter}`;
        }
      } else {
        // Normal kullanıcı için kendi mağaza tipini kullan
        if (userStoreType) {
          storeTypeParam = `?store_type=${userStoreType}`;
        }
      }
      const response = await fetch(`${API_URL}/campaigns${storeTypeParam}`);
      const data = await response.json();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Kampanya yükleme hatası:', error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/products`);
      const data = await response.json();
      // API {products: [...]} şeklinde dönüyor
      const productList = data.products || data;
      setProducts(Array.isArray(productList) ? productList : []);
    } catch (error) {
      console.error('Ürün yükleme hatası:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) {
      toast.warning('Kampanya adı ve tarihler gerekli!');
      return;
    }

    try {
      const payload = {
        ...formData,
        conditions: JSON.stringify(formData.conditions),
        rewards: JSON.stringify(formData.rewards),
        // Tarihlere saat ekle - timezone kaymasını önle
        start_date: formData.start_date + 'T12:00:00',
        end_date: formData.end_date + 'T12:00:00'
      };

      const url = editingCampaign
        ? `${API_URL}/campaigns/${editingCampaign.id}`
        : `${API_URL}/campaigns`;

      const method = editingCampaign ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        const kampanyaTuru = CAMPAIGN_TYPES[formData.campaign_type]?.label || formData.campaign_type;

        if (editingCampaign) {
          // Değişiklikleri tespit et
          const changes = [];
          const eskiKampanyaTuru = CAMPAIGN_TYPES[originalCampaign?.campaign_type]?.label || originalCampaign?.campaign_type;

          if (originalCampaign?.name !== formData.name) {
            changes.push(`Kampanya Adı: "${originalCampaign?.name}" → "${formData.name}"`);
          }
          if (originalCampaign?.description !== formData.description) {
            changes.push(`Açıklama güncellendi`);
          }
          if (originalCampaign?.campaign_type !== formData.campaign_type) {
            changes.push(`Kampanya Türü: ${eskiKampanyaTuru} → ${kampanyaTuru}`);
          }
          if (originalCampaign?.start_date !== formData.start_date) {
            changes.push(`Başlangıç: ${formatDateTR(originalCampaign?.start_date)} → ${formatDateTR(formData.start_date)}`);
          }
          if (originalCampaign?.end_date !== formData.end_date) {
            changes.push(`Bitiş: ${formatDateTR(originalCampaign?.end_date)} → ${formatDateTR(formData.end_date)}`);
          }
          if (originalCampaign?.usage_limit !== formData.usage_limit) {
            changes.push(`Kullanım Limiti: ${originalCampaign?.usage_limit || 'Sınırsız'} → ${formData.usage_limit || 'Sınırsız'}`);
          }
          if (originalCampaign?.ecommerce_active !== formData.ecommerce_active) {
            changes.push(`E-ticaret: ${originalCampaign?.ecommerce_active ? 'Açık' : 'Kapalı'} → ${formData.ecommerce_active ? 'Açık' : 'Kapalı'}`);
          }
          // İndirim değişiklikleri
          if (originalCampaign?.rewards?.discount_percent !== formData.rewards.discount_percent) {
            changes.push(`İndirim Oranı: %${originalCampaign?.rewards?.discount_percent || 0} → %${formData.rewards.discount_percent}`);
          }
          if (originalCampaign?.rewards?.discount_amount !== formData.rewards.discount_amount) {
            changes.push(`İndirim Tutarı: ₺${originalCampaign?.rewards?.discount_amount || 0} → ₺${formData.rewards.discount_amount}`);
          }
          // Min alışveriş değişikliği
          if (originalCampaign?.conditions?.min_purchase !== formData.conditions.min_purchase) {
            changes.push(`Min. Alışveriş: ₺${originalCampaign?.conditions?.min_purchase || 0} → ₺${formData.conditions.min_purchase}`);
          }

          logService.log('campaigns', 'update',
            `Kampanya güncellendi: ${formData.name} - ${changes.length} değişiklik`, {
            entity_type: 'campaign',
            entity_id: editingCampaign.id,
            details: {
              kampanya_adi: formData.name,
              kampanya_turu: kampanyaTuru,
              degisiklik_sayisi: changes.length,
              degisiklikler: changes.length > 0 ? changes : ['Değişiklik yapılmadı'],
              onceki_degerler: {
                ad: originalCampaign?.name,
                tur: eskiKampanyaTuru,
                baslangic: formatDateTR(originalCampaign?.start_date),
                bitis: formatDateTR(originalCampaign?.end_date),
                indirim: originalCampaign?.rewards?.discount_percent ? `%${originalCampaign.rewards.discount_percent}` : `₺${originalCampaign?.rewards?.discount_amount || 0}`
              },
              yeni_degerler: {
                ad: formData.name,
                tur: kampanyaTuru,
                baslangic: formatDateTR(formData.start_date),
                bitis: formatDateTR(formData.end_date),
                indirim: formData.rewards.discount_percent ? `%${formData.rewards.discount_percent}` : `₺${formData.rewards.discount_amount}`
              }
            },
            status: 'success'
          });
        } else {
          // Yeni kampanya log'u
          logService.log('campaigns', 'create',
            `Yeni kampanya oluşturuldu: ${formData.name} (${kampanyaTuru})`, {
            entity_type: 'campaign',
            entity_id: data.campaign?.id,
            details: {
              kampanya_adi: formData.name,
              kampanya_turu: kampanyaTuru,
              aciklama: formData.description || '-',
              baslangic_tarihi: formatDateTR(formData.start_date),
              bitis_tarihi: formatDateTR(formData.end_date),
              indirim_orani: formData.rewards.discount_percent ? `%${formData.rewards.discount_percent}` : null,
              indirim_tutari: formData.rewards.discount_amount ? `₺${formData.rewards.discount_amount}` : null,
              min_alisveris: formData.conditions.min_purchase ? `₺${formData.conditions.min_purchase}` : 'Yok',
              turler: formData.conditions.metal_types?.length > 0 ? formData.conditions.metal_types.map(mt => metalTypeLabels[mt] || mt).join(', ') : 'Tümü',
              kategoriler: formData.conditions.categories?.length > 0 ? formData.conditions.categories.join(', ') : 'Tümü',
              markalar: formData.conditions.brands?.length > 0 ? formData.conditions.brands.join(', ') : 'Tümü',
              kullanim_limiti: formData.usage_limit || 'Sınırsız',
              durum: formData.active ? 'Aktif' : 'Pasif'
            },
            status: 'success'
          });
        }
        toast.success(editingCampaign ? 'Kampanya güncellendi!' : 'Kampanya oluşturuldu!');
        if (formData.ecommerce_active && data.shopify?.success) {
          toast.success('Kampanya Shopify İndirimler sayfasına gönderildi!');
        } else if (formData.ecommerce_active && data.shopify && !data.shopify.success) {
          toast.warning(`Kampanya kaydedildi ama Shopify'a gönderilemedi: ${data.shopify.error || 'Bilinmeyen hata'}`);
        }
        if (!formData.ecommerce_active && data.shopify?.deleted) {
          toast.info('Kampanya Shopify İndirimler sayfasından kaldırıldı.');
        }
        setShowModal(false);
        resetForm();
        fetchCampaigns();
      } else {
        toast.error(data.error || 'İşlem başarısız!');
        logService.log('campaigns', editingCampaign ? 'update' : 'create',
          `Kampanya ${editingCampaign ? 'güncellenemedi' : 'oluşturulamadı'}: ${formData.name}`, {
          entity_type: 'campaign',
          details: { kampanya_adi: formData.name, hata: data.error },
          status: 'error'
        });
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('campaigns', editingCampaign ? 'update' : 'create',
        `Kampanya hatası: ${formData.name}`, {
        entity_type: 'campaign',
        details: { kampanya_adi: formData.name, hata: error.message },
        status: 'error'
      });
    }
  };

  const handleToggleActive = async (campaign) => {
    const kampanyaTuru = CAMPAIGN_TYPES[campaign.campaign_type]?.label || campaign.campaign_type;
    try {
      const response = await fetch(`${API_URL}/campaigns/${campaign.id}/toggle`, {
        method: 'PATCH'
      });
      const data = await response.json();
      if (data.success) {
        const newStatus = !campaign.active;
        logService.log('campaigns', 'update',
          `Kampanya ${newStatus ? 'aktifleştirildi' : 'pasifleştirildi'}: ${campaign.name}`, {
          entity_type: 'campaign',
          entity_id: campaign.id,
          details: {
            kampanya_adi: campaign.name,
            kampanya_turu: kampanyaTuru,
            onceki_durum: campaign.active ? 'Aktif' : 'Pasif',
            yeni_durum: newStatus ? 'Aktif' : 'Pasif',
            baslangic: formatDateTR(campaign.start_date),
            bitis: formatDateTR(campaign.end_date)
          },
          status: 'success'
        });
        fetchCampaigns();
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      logService.log('campaigns', 'update', `Kampanya durum değiştirme hatası: ${campaign.name}`, {
        entity_type: 'campaign',
        entity_id: campaign.id,
        details: { kampanya_adi: campaign.name, hata: error.message },
        status: 'error'
      });
    }
  };

  const handleDelete = async (campaign) => {
    showConfirmation({
      title: 'Kampanya Sil',
      message: 'Bu kampanyayı silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        const kampanyaTuru = CAMPAIGN_TYPES[campaign.campaign_type]?.label || campaign.campaign_type;
        let rewards = {};
        try {
          rewards = typeof campaign.rewards === 'string' ? JSON.parse(campaign.rewards) : campaign.rewards || {};
        } catch (e) {}

        try {
          const response = await fetch(`${API_URL}/campaigns/${campaign.id}`, {
            method: 'DELETE'
          });
          const data = await response.json();
          if (data.success) {
            logService.log('campaigns', 'delete',
              `Kampanya silindi: ${campaign.name} (${kampanyaTuru})`, {
              entity_type: 'campaign',
              entity_id: campaign.id,
              details: {
                kampanya_adi: campaign.name,
                kampanya_turu: kampanyaTuru,
                aciklama: campaign.description || '-',
                baslangic_tarihi: formatDateTR(campaign.start_date),
                bitis_tarihi: formatDateTR(campaign.end_date),
                indirim: rewards.discount_percent ? `%${rewards.discount_percent}` : `₺${rewards.discount_amount || 0}`,
                kullanim_sayisi: campaign.usage_count || 0,
                durum: campaign.active ? 'Aktif' : 'Pasif'
              },
              status: 'success'
            });
            toast.success('Kampanya silindi!');
            fetchCampaigns();
          } else {
            toast.error(data.error || 'Silme başarısız!');
            logService.log('campaigns', 'delete', `Kampanya silinemedi: ${campaign.name}`, {
              entity_type: 'campaign',
              entity_id: campaign.id,
              details: { kampanya_adi: campaign.name, hata: data.error },
              status: 'error'
            });
          }
        } catch (error) {
          console.error('Silme hatası:', error);
          toast.error('Silme hatası!');
          logService.log('campaigns', 'delete', `Kampanya silme hatası: ${campaign.name}`, {
            entity_type: 'campaign',
            entity_id: campaign.id,
            details: { kampanya_adi: campaign.name, hata: error.message },
            status: 'error'
          });
        }
      }
    });
  };

  const openEditModal = (campaign) => {
    setEditingCampaign(campaign);

    let conditions = {};
    let rewards = {};

    try {
      conditions = typeof campaign.conditions === 'string'
        ? JSON.parse(campaign.conditions)
        : campaign.conditions || {};
      rewards = typeof campaign.rewards === 'string'
        ? JSON.parse(campaign.rewards)
        : campaign.rewards || {};
    } catch (e) {
      console.error('Parse hatası:', e);
    }

    // Orijinal değerleri kaydet (değişiklik tespiti için)
    setOriginalCampaign({
      name: campaign.name || '',
      description: campaign.description || '',
      campaign_type: campaign.campaign_type || 'percentage',
      store_type: campaign.store_type || 'all',
      conditions: { ...conditions },
      rewards: { ...rewards },
      start_date: campaign.start_date ? campaign.start_date.split('T')[0] : '',
      end_date: campaign.end_date ? campaign.end_date.split('T')[0] : '',
      priority: campaign.priority || 0,
      usage_limit: campaign.usage_limit || null,
      active: campaign.active === 1 || campaign.active === true,
      ecommerce_active: campaign.ecommerce_active === 1 || campaign.ecommerce_active === true
    });

    setFormData({
      name: campaign.name || '',
      description: campaign.description || '',
      campaign_type: campaign.campaign_type || 'percentage',
      store_type: campaign.store_type || 'all',
      conditions: {
        min_purchase: conditions.min_purchase || 0,
        min_purchase_type: conditions.min_purchase_type || 'category',
        metal_types: conditions.metal_types || [],
        categories: conditions.categories || [],
        brands: conditions.brands || [],
        products: conditions.products || [],
        buy_quantity: conditions.buy_quantity || 3,
        required_products: conditions.required_products || [],
        special_dates: conditions.special_dates || [],
        birthday_days_before: conditions.birthday_days_before || 0,
        birthday_days_after: conditions.birthday_days_after || 0
      },
      rewards: {
        discount_percent: rewards.discount_percent || 10,
        discount_amount: rewards.discount_amount || 0,
        discount_type: rewards.discount_type || 'percentage',
        free_quantity: rewards.free_quantity || 1,
        gift_product_id: rewards.gift_product_id || null,
        gift_product_name: rewards.gift_product_name || '',
        tiers: rewards.tiers || [
          { item_number: 1, discount_percent: 10 },
          { item_number: 2, discount_percent: 20 },
          { item_number: 3, discount_percent: 30 }
        ],
        repeat: rewards.repeat || false
      },
      start_date: campaign.start_date ? campaign.start_date.split('T')[0] : '',
      end_date: campaign.end_date ? campaign.end_date.split('T')[0] : '',
      priority: campaign.priority || 0,
      usage_limit: campaign.usage_limit || null,
      active: campaign.active === 1 || campaign.active === true,
      ecommerce_active: campaign.ecommerce_active === 1 || campaign.ecommerce_active === true
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCampaign(null);
    setOriginalCampaign(null);
    setFormData({
      name: '',
      description: '',
      campaign_type: 'percentage',
      conditions: {
        min_purchase: 0,
        min_purchase_type: 'category',
        categories: [],
        products: [],
        buy_quantity: 3,
        required_products: [],
        special_dates: [],
        birthday_days_before: 0,
        birthday_days_after: 0
      },
      rewards: {
        discount_percent: 10,
        discount_amount: 0,
        discount_type: 'percentage',
        free_quantity: 1,
        gift_product_id: null,
        gift_product_name: '',
        tiers: [
          { item_number: 1, discount_percent: 10 },
          { item_number: 2, discount_percent: 20 },
          { item_number: 3, discount_percent: 30 }
        ],
        repeat: false
      },
      start_date: '',
      end_date: '',
      priority: 0,
      usage_limit: null,
      active: true,
      ecommerce_active: false
    });
  };

  const metalTypeLabels = { gold: 'Altın', silver: 'Gümüş', steel: 'Çelik', brands: 'Markalar', aksesuar: 'Aksesuar', sarf: 'Sarf', zippo: 'Zippo' };

  const toggleMetalType = (mt) => {
    const current = formData.conditions.metal_types || [];
    const updated = current.includes(mt) ? current.filter(m => m !== mt) : [...current, mt];
    setFormData({
      ...formData,
      conditions: { ...formData.conditions, metal_types: updated }
    });
  };

  // Seçili store_type'a göre mevcut metal türlerini getir
  const getMetalTypesForStoreType = () => {
    const st = formData.store_type;
    const allTypes = Object.keys(dbCategories).filter(mt => mt !== 'all');
    if (!st || st === 'all') return allTypes;
    if (st === 'gold') return allTypes.filter(mt => mt === 'gold');
    if (st === 'silver') return allTypes.filter(mt => mt !== 'gold');
    if (st.startsWith('store_')) {
      const storeId = parseInt(st.replace('store_', ''));
      const store = stores.find(s => s.id === storeId);
      if (store?.metal_type === 'gold') return allTypes.filter(mt => mt === 'gold');
      return allTypes.filter(mt => mt !== 'gold');
    }
    return allTypes;
  };

  // Tür + kategori seçicisi render helper
  const renderTypeAndCategorySelector = () => {
    const metalTypes = getMetalTypesForStoreType();
    const selectedMetalTypes = formData.conditions.metal_types || [];
    return (
      <>
        {metalTypes.length > 1 && (
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: '6px', display: 'block' }}>Ürün Türü</label>
            <div className="categories-selector">
              {metalTypes.map(mt => (
                <button
                  key={mt}
                  type="button"
                  className={`category-btn ${selectedMetalTypes.includes(mt) ? 'selected' : ''}`}
                  onClick={() => toggleMetalType(mt)}
                  style={selectedMetalTypes.includes(mt) ? { background: '#0f766e', color: '#fff', borderColor: '#0f766e' } : {}}
                >
                  {selectedMetalTypes.includes(mt) ? '✓ ' : ''}{metalTypeLabels[mt] || mt}
                </button>
              ))}
            </div>
          </div>
        )}
        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: '6px', display: 'block' }}>Kategori</label>
          <div className="categories-selector">
            {getCategoriesForStoreType().filter(cat => {
              if (selectedMetalTypes.length === 0) return true;
              return selectedMetalTypes.some(mt => (dbCategories[mt] || []).includes(cat));
            }).map(cat => (
              <button
                key={cat}
                type="button"
                className={`category-btn ${formData.conditions.categories?.includes(cat) ? 'selected' : ''}`}
                onClick={() => toggleCategory(cat)}
              >
                {formData.conditions.categories?.includes(cat) ? '✓ ' : ''}{cat}
              </button>
            ))}
          </div>
        </div>
        {getBrandsForSelection().length > 0 && (
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: '6px', display: 'block' }}>Marka</label>
            <div className="categories-selector" style={{ maxHeight: '120px', overflowY: 'auto' }}>
              {getBrandsForSelection().map(brand => (
                <button
                  key={brand}
                  type="button"
                  className={`category-btn ${(formData.conditions.brands || []).includes(brand) ? 'selected' : ''}`}
                  onClick={() => toggleBrand(brand)}
                  style={(formData.conditions.brands || []).includes(brand) ? { background: '#7c3aed', color: '#fff', borderColor: '#7c3aed' } : {}}
                >
                  {(formData.conditions.brands || []).includes(brand) ? '✓ ' : ''}{brand}
                </button>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const toggleBrand = (brand) => {
    const current = formData.conditions.brands || [];
    const updated = current.includes(brand) ? current.filter(b => b !== brand) : [...current, brand];
    setFormData({
      ...formData,
      conditions: { ...formData.conditions, brands: updated }
    });
  };

  // Seçili filtrelere göre markaları getir
  const getBrandsForSelection = () => {
    const st = formData.store_type;
    const selectedMetalTypes = formData.conditions.metal_types || [];
    const all = new Set();

    const addBrands = (mt) => { (dbBrands[mt] || []).forEach(b => all.add(b)); };

    if (selectedMetalTypes.length > 0) {
      selectedMetalTypes.forEach(addBrands);
    } else if (!st || st === 'all') {
      Object.keys(dbBrands).forEach(addBrands);
    } else if (st === 'gold') {
      addBrands('gold');
    } else if (st === 'silver') {
      Object.keys(dbBrands).filter(mt => mt !== 'gold').forEach(addBrands);
    } else if (st.startsWith('store_')) {
      const storeId = parseInt(st.replace('store_', ''));
      const store = stores.find(s => s.id === storeId);
      if (store?.metal_type === 'gold') addBrands('gold');
      else Object.keys(dbBrands).filter(mt => mt !== 'gold').forEach(addBrands);
    }
    return [...all].sort();
  };

  const toggleCategory = (category) => {
    const current = formData.conditions.categories || [];
    const updated = current.includes(category)
      ? current.filter(c => c !== category)
      : [...current, category];
    setFormData({
      ...formData,
      conditions: { ...formData.conditions, categories: updated }
    });
  };

  const toggleProduct = (productId) => {
    const current = formData.conditions.products || [];
    const updated = current.includes(productId)
      ? current.filter(p => p !== productId)
      : [...current, productId];
    setFormData({
      ...formData,
      conditions: { ...formData.conditions, products: updated }
    });
  };

  const toggleRequiredProduct = (productId) => {
    const current = formData.conditions.required_products || [];
    const updated = current.includes(productId)
      ? current.filter(p => p !== productId)
      : [...current, productId];
    setFormData({
      ...formData,
      conditions: { ...formData.conditions, required_products: updated }
    });
  };

  const updateTier = (index, field, value) => {
    const tiers = [...formData.rewards.tiers];
    tiers[index] = { ...tiers[index], [field]: parseInt(value) || 0 };
    setFormData({
      ...formData,
      rewards: { ...formData.rewards, tiers }
    });
  };

  const addTier = () => {
    const tiers = [...formData.rewards.tiers];
    const lastTier = tiers[tiers.length - 1];
    tiers.push({
      item_number: (lastTier?.item_number || 0) + 1,
      discount_percent: (lastTier?.discount_percent || 0) + 10
    });
    setFormData({
      ...formData,
      rewards: { ...formData.rewards, tiers }
    });
  };

  const removeTier = (index) => {
    if (formData.rewards.tiers.length <= 1) return;
    const tiers = formData.rewards.tiers.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      rewards: { ...formData.rewards, tiers }
    });
  };

  const selectGiftProduct = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    setFormData({
      ...formData,
      rewards: {
        ...formData.rewards,
        gift_product_id: productId ? parseInt(productId) : null,
        gift_product_name: product?.name || ''
      }
    });
  };

  const getCampaignStatus = (campaign) => {
    const now = new Date();
    now.setHours(12, 0, 0, 0); // Gün ortası olarak ayarla

    // Tarihi timezone sorunu olmadan parse et
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day, 12, 0, 0); // Gün ortası
    };

    const start = parseDate(campaign.start_date);
    const end = parseDate(campaign.end_date);

    if (!campaign.active) {
      return { label: 'Pasif', class: 'status-inactive' };
    }
    if (start && now < start) {
      return { label: 'Beklemede', class: 'status-pending' };
    }
    if (end && now > end) {
      return { label: 'Sona Erdi', class: 'status-expired' };
    }
    return { label: 'Aktif', class: 'status-active' };
  };

  const getCampaignSummary = (campaign) => {
    let rewards = {};
    try {
      rewards = typeof campaign.rewards === 'string'
        ? JSON.parse(campaign.rewards)
        : campaign.rewards || {};
    } catch (e) {}

    let conditions = {};
    try {
      conditions = typeof campaign.conditions === 'string'
        ? JSON.parse(campaign.conditions)
        : campaign.conditions || {};
    } catch (e) {}

    switch (campaign.campaign_type) {
      case 'percentage':
        return `%${rewards.discount_percent || 0} indirim`;
      case 'fixed_amount':
        return `₺${rewards.discount_amount || 0} indirim`;
      case 'progressive':
        const tiers = rewards.tiers || [];
        return tiers.map(t => `${t.item_number}. ürün %${t.discount_percent}`).join(', ');
      case 'buy_x_pay_y':
        return `${conditions.buy_quantity || 3} al ${(conditions.buy_quantity || 3) - (rewards.free_quantity || 1)} öde`;
      case 'bundle':
        return 'Set kampanyası';
      case 'gift_with_purchase':
        return `Hediye: ${rewards.gift_product_name || 'Belirlenmedi'}`;
      case 'category_discount':
        const catNames = conditions.categories || [];
        const catText = catNames.length > 0
          ? (catNames.length <= 2 ? catNames.join(', ') : `${catNames.slice(0, 2).join(', ')} +${catNames.length - 2}`)
          : 'Tüm kategoriler';
        return `${catText} %${rewards.discount_percent || 0} indirim`;
      case 'product_discount':
        const prodIds = conditions.products || [];
        const prodCount = prodIds.length;
        const prodText = prodCount > 0 ? `${prodCount} ürün` : 'Tüm ürünler';
        return `${prodText} %${rewards.discount_percent || 0} indirim`;
      case 'special_day':
        return rewards.discount_type === 'fixed'
          ? `Özel gün: ₺${rewards.discount_amount || 0} indirim`
          : `Özel gün: %${rewards.discount_percent || 0} indirim`;
      case 'birthday':
        const daysBefore = conditions.birthday_days_before || 0;
        const daysAfter = conditions.birthday_days_after || 0;
        const discountText = rewards.discount_type === 'fixed'
          ? `₺${rewards.discount_amount || 0}`
          : `%${rewards.discount_percent || 0}`;
        return `Doğum günü: ${discountText} (${daysBefore > 0 || daysAfter > 0 ? `±${Math.max(daysBefore, daysAfter)} gün` : 'aynı gün'})`;
      default:
        return campaign.campaign_type;
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getCampaignStatus(campaign);

    if (filterStatus === 'all') return matchesSearch;
    if (filterStatus === 'active') return matchesSearch && status.label === 'Aktif';
    if (filterStatus === 'inactive') return matchesSearch && (status.label === 'Pasif' || status.label === 'Sona Erdi');
    if (filterStatus === 'pending') return matchesSearch && status.label === 'Beklemede';

    return matchesSearch;
  });

  const stats = {
    total: campaigns.length,
    active: campaigns.filter(c => getCampaignStatus(c).label === 'Aktif').length,
    pending: campaigns.filter(c => getCampaignStatus(c).label === 'Beklemede').length,
    expired: campaigns.filter(c => getCampaignStatus(c).label === 'Sona Erdi' || getCampaignStatus(c).label === 'Pasif').length
  };

  // Kampanya türüne göre form alanları
  const renderTypeSpecificFields = () => {
    const type = formData.campaign_type;

    return (
      <>
        {/* Yüzde veya Tutar İndirimi */}
        {(type === 'percentage' || type === 'category_discount' || type === 'product_discount') && (
          <div className="form-section">
            <h3>İndirim Oranı</h3>
            <div className="form-group">
              <label>İndirim Yüzdesi (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.rewards.discount_percent}
                onChange={(e) => setFormData({
                  ...formData,
                  rewards: { ...formData.rewards, discount_percent: parseInt(e.target.value) || 0 }
                })}
              />
            </div>
          </div>
        )}

        {type === 'fixed_amount' && (
          <div className="form-section">
            <h3>İndirim Tutarı</h3>
            <div className="form-group">
              <label>İndirim Tutarı (₺)</label>
              <PriceInput
                value={formData.rewards.discount_amount || 0}
                onChange={(val) => setFormData({
                  ...formData,
                  rewards: { ...formData.rewards, discount_amount: val }
                })}
              />
            </div>
            <div className="form-group">
              <label>Minimum Alışveriş Tutarı (₺)</label>
              <PriceInput
                value={formData.conditions.min_purchase || 0}
                onChange={(val) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, min_purchase: val }
                })}
                placeholder="0 = Koşul yok"
              />
            </div>
            <div className="repeat-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.rewards.repeat || false}
                  onChange={(e) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, repeat: e.target.checked }
                  })}
                />
                <span>İndirimi tekrarla</span>
              </label>
              <p className="helper-text">
                Aktif olduğunda: Min. tutar her karşılandığında indirim tekrarlanır.
                Örn: 10.000₺'de 1.000₺ indirim → 20.000₺'de 2.000₺, 30.000₺'de 3.000₺...
              </p>
            </div>
          </div>
        )}

        {/* Kademeli İndirim */}
        {type === 'progressive' && (
          <div className="form-section">
            <h3>Kademeli İndirim Oranları</h3>
            <p className="helper-text">Kampanyanın geçerli olacağı tür/kategorileri seçin (boş bırakılırsa tüm ürünlerde geçerlidir)</p>
            {renderTypeAndCategorySelector()}
            <p className="helper-text" style={{ marginTop: '16px' }}>Her ürün için farklı indirim oranı belirleyin</p>
            <div className="tiers-list">
              {formData.rewards.tiers.map((tier, index) => (
                <div key={index} className="tier-row">
                  <div className="tier-item">
                    <label>{tier.item_number}. Ürün</label>
                    <div className="tier-input">
                      <span>%</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={tier.discount_percent}
                        onChange={(e) => updateTier(index, 'discount_percent', e.target.value)}
                      />
                    </div>
                  </div>
                  {formData.rewards.tiers.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove-tier"
                      onClick={() => removeTier(index)}
                    >
                      Kaldır
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" className="btn-add-tier" onClick={addTier}>
              + Kademe Ekle
            </button>

            <div className="repeat-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.rewards.repeat || false}
                  onChange={(e) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, repeat: e.target.checked }
                  })}
                />
                <span>Kademeleri tekrarla</span>
              </label>
              <p className="helper-text">
                Aktif olduğunda: 1. ürün %10, 2. ürün %20, 3. ürün %30, 4. ürün tekrar %10, 5. ürün %20...
              </p>
            </div>
          </div>
        )}

        {/* X Al Y Öde */}
        {type === 'buy_x_pay_y' && (
          <div className="form-section">
            <h3>Al - Öde Ayarları</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Alınacak Adet</label>
                <input
                  type="number"
                  min="2"
                  value={formData.conditions.buy_quantity}
                  onChange={(e) => setFormData({
                    ...formData,
                    conditions: { ...formData.conditions, buy_quantity: parseInt(e.target.value) || 2 }
                  })}
                />
              </div>
              <div className="form-group">
                <label>Bedava Adet</label>
                <input
                  type="number"
                  min="1"
                  max={formData.conditions.buy_quantity - 1}
                  value={formData.rewards.free_quantity}
                  onChange={(e) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, free_quantity: parseInt(e.target.value) || 1 }
                  })}
                />
              </div>
            </div>
            <p className="campaign-preview">
              Önizleme: <strong>{formData.conditions.buy_quantity} Al {formData.conditions.buy_quantity - formData.rewards.free_quantity} Öde</strong>
            </p>

            <div className="repeat-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.rewards.repeat || false}
                  onChange={(e) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, repeat: e.target.checked }
                  })}
                />
                <span>Kampanyayı tekrarla</span>
              </label>
              <p className="helper-text">
                Aktif olduğunda: 3 Al 2 Öde → 6 ürün alınca 4 öde, 9 ürün alınca 6 öde...
              </p>
            </div>
          </div>
        )}

        {/* Set/Bundle Kampanyası */}
        {type === 'bundle' && (
          <div className="form-section">
            <h3>Set İçeriği</h3>
            <p className="helper-text">Sete dahil edilecek tür ve kategorileri seçin</p>
            {renderTypeAndCategorySelector()}
            <div className="form-group" style={{marginTop: '16px'}}>
              <label>Set İndirimi (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.rewards.discount_percent}
                onChange={(e) => setFormData({
                  ...formData,
                  rewards: { ...formData.rewards, discount_percent: parseInt(e.target.value) || 0 }
                })}
              />
            </div>
          </div>
        )}

        {/* Hediye Kampanyası */}
        {type === 'gift_with_purchase' && (
          <div className="form-section">
            <h3>Hediye Ayarları</h3>
            <div className="form-group">
              <label>Minimum Alışveriş Tutarı (₺)</label>
              <PriceInput
                value={formData.conditions.min_purchase || 0}
                onChange={(val) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, min_purchase: val }
                })}
              />
            </div>
            <p className="helper-text">Veya belirli tür/kategorilerden alışveriş yapılırsa:</p>
            {renderTypeAndCategorySelector()}

            {/* Hediye Ürün Seçimi - Geliştirilmiş */}
            <div className="gift-product-section">
              <label>Hediye Ürün Seçimi</label>

              {/* Seçili Ürün Gösterimi */}
              {formData.rewards.gift_product_id && (
                <div className="selected-gift-product">
                  <span className="selected-label">Seçili Hediye:</span>
                  <span className="selected-name">{formData.rewards.gift_product_name}</span>
                  <button
                    type="button"
                    className="btn-clear-gift"
                    onClick={() => selectGiftProduct('')}
                  >
                    ✕ Kaldır
                  </button>
                </div>
              )}

              {/* Arama ve Filtre */}
              <div className="gift-product-filters">
                <div className="gift-search-box">
                  <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Ürün ara (isim, barkod)..."
                    value={giftProductSearch}
                    onChange={(e) => setGiftProductSearch(e.target.value)}
                  />
                  {giftProductSearch && (
                    <button
                      type="button"
                      className="btn-clear-search"
                      onClick={() => setGiftProductSearch('')}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <select
                  className="gift-category-filter"
                  value={giftProductCategory}
                  onChange={(e) => setGiftProductCategory(e.target.value)}
                >
                  <option value="all">Tüm Kategoriler</option>
                  {[...new Set(products.map(p => p.category).filter(Boolean))].sort().map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Ürün Listesi */}
              <div className="gift-products-grid">
                {products
                  .filter(product => {
                    const matchesSearch = !giftProductSearch ||
                      product.name?.toLowerCase().includes(giftProductSearch.toLowerCase()) ||
                      product.barcode?.toLowerCase().includes(giftProductSearch.toLowerCase()) ||
                      product.stock_code?.toLowerCase().includes(giftProductSearch.toLowerCase());
                    const matchesCategory = giftProductCategory === 'all' ||
                      product.category === giftProductCategory;
                    return matchesSearch && matchesCategory;
                  })
                  .map(product => (
                    <div
                      key={product.id}
                      className={`gift-product-card ${formData.rewards.gift_product_id === product.id ? 'selected' : ''}`}
                      onClick={() => selectGiftProduct(product.id)}
                    >
                      {product.image ? (
                        <img
                          src={`${API_HOST}${product.image}`}
                          alt={product.name}
                          className="gift-product-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="gift-product-placeholder" style={{display: product.image ? 'none' : 'flex'}}>
                        {product.category?.charAt(0) || 'Ü'}
                      </div>
                      <div className="gift-product-info">
                        <span className="gift-product-name">{product.name}</span>
                        <span className="gift-product-category">{product.category}</span>
                        <span className="gift-product-price">₺{Number(product.sell_price || 0).toLocaleString('tr-TR')}</span>
                      </div>
                      {formData.rewards.gift_product_id === product.id && (
                        <div className="gift-selected-badge">✓</div>
                      )}
                    </div>
                  ))
                }
                {products.filter(product => {
                  const matchesSearch = !giftProductSearch ||
                    product.name?.toLowerCase().includes(giftProductSearch.toLowerCase()) ||
                    product.barcode?.toLowerCase().includes(giftProductSearch.toLowerCase());
                  const matchesCategory = giftProductCategory === 'all' ||
                    product.category === giftProductCategory;
                  return matchesSearch && matchesCategory;
                }).length === 0 && (
                  <div className="gift-products-empty">
                    Ürün bulunamadı
                  </div>
                )}
              </div>

              <p className="gift-product-count">
                {products.filter(product => {
                  const matchesSearch = !giftProductSearch ||
                    product.name?.toLowerCase().includes(giftProductSearch.toLowerCase()) ||
                    product.barcode?.toLowerCase().includes(giftProductSearch.toLowerCase());
                  const matchesCategory = giftProductCategory === 'all' ||
                    product.category === giftProductCategory;
                  return matchesSearch && matchesCategory;
                }).length} ürün listeleniyor
              </p>
            </div>

            {/* Tekrarlanabilirlik Seçeneği */}
            <div className="repeat-option" style={{marginTop: '16px'}}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.rewards.repeat || false}
                  onChange={(e) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, repeat: e.target.checked }
                  })}
                />
                <span>Hediyeyi tekrarla</span>
              </label>
              <p className="helper-text">
                Aktif olduğunda: Min. tutar her karşılandığında hediye tekrarlanır.
                Örn: 10.000₺'de 1 hediye → 20.000₺'de 2 hediye, 30.000₺'de 3 hediye...
              </p>
            </div>
          </div>
        )}

        {/* Yüzde İndirim için kategori seçimi (opsiyonel) */}
        {type === 'percentage' && (
          <div className="form-section">
            <h3>Geçerli Kategoriler</h3>
            <p className="helper-text">Hiç seçim yapılmazsa tüm ürünlere uygulanır</p>
            {renderTypeAndCategorySelector()}
          </div>
        )}

        {/* Kategori İndirimi - Ürünlerden gelen gerçek kategoriler */}
        {type === 'category_discount' && (
          <div className="form-section">
            <h3>İndirim Uygulanacak Kategoriler</h3>
            <p className="helper-text">Sadece seçilen tür/kategorilerdeki ürünlere indirim uygulanır</p>
            {renderTypeAndCategorySelector()}
            {formData.conditions.categories?.length > 0 && (
              <p className="selected-count">
                {formData.conditions.categories.length} kategori seçildi
              </p>
            )}

            {/* Minimum Tutar */}
            <div className="form-group" style={{marginTop: '16px'}}>
              <label>Minimum Alışveriş Tutarı (₺)</label>
              <PriceInput
                value={formData.conditions.min_purchase || 0}
                onChange={(val) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, min_purchase: val }
                })}
                placeholder="0 = Koşul yok"
              />
            </div>

            {/* Minimum Tutar Tipi */}
            {formData.conditions.min_purchase > 0 && (
              <div className="form-group" style={{marginTop: '12px'}}>
                <label>Minimum Tutar Kontrolü</label>
                <div className="min-purchase-type-selector">
                  <button
                    type="button"
                    className={`type-btn ${formData.conditions.min_purchase_type === 'general' ? 'selected' : ''}`}
                    onClick={() => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, min_purchase_type: 'general' }
                    })}
                  >
                    <span className="type-icon">🛒</span>
                    <span className="type-label">Genel Sepet</span>
                    <span className="type-desc">Tüm sepet toplamı</span>
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${!formData.conditions.min_purchase_type || formData.conditions.min_purchase_type === 'category' ? 'selected' : ''}`}
                    onClick={() => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, min_purchase_type: 'category' }
                    })}
                  >
                    <span className="type-icon">📁</span>
                    <span className="type-label">Sadece Kategori</span>
                    <span className="type-desc">Seçilen kategoriler toplamı</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Ürün İndirimi - Ürün seçimi */}
        {type === 'product_discount' && (
          <div className="form-section">
            <h3>İndirim Uygulanacak Ürünler</h3>
            <p className="helper-text">Sadece seçilen ürünlere indirim uygulanır</p>

            {/* Seçili ürünler listesi */}
            {formData.conditions.products?.length > 0 && (
              <div className="selected-products-list" style={{marginBottom: '16px'}}>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                  {formData.conditions.products.map(productId => {
                    const product = products.find(p => p.id === productId);
                    return product ? (
                      <div key={productId} className="selected-product-tag">
                        <span>{product.name}</span>
                        <button
                          type="button"
                          onClick={() => toggleProduct(productId)}
                          style={{marginLeft: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444'}}
                        >
                          ✕
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
                <p className="selected-count">{formData.conditions.products.length} ürün seçildi</p>
              </div>
            )}

            {/* Arama ve filtre */}
            <div className="gift-product-filters">
              <div className="gift-search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Ürün ara (isim veya barkod)..."
                  value={giftProductSearch}
                  onChange={(e) => setGiftProductSearch(e.target.value)}
                />
                {giftProductSearch && (
                  <button className="btn-clear-search" onClick={() => setGiftProductSearch('')}>✕</button>
                )}
              </div>
              <select
                className="gift-category-filter"
                value={giftProductCategory}
                onChange={(e) => setGiftProductCategory(e.target.value)}
              >
                <option value="all">Tüm Kategoriler</option>
                {[...new Set(products.map(p => p.category).filter(Boolean))].sort().map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Ürün grid */}
            <div className="gift-products-grid">
              {products
                .filter(p => {
                  const matchesSearch = !giftProductSearch ||
                    p.name?.toLowerCase().includes(giftProductSearch.toLowerCase()) ||
                    p.barcode?.includes(giftProductSearch);
                  const matchesCategory = giftProductCategory === 'all' || p.category === giftProductCategory;
                  return matchesSearch && matchesCategory;
                })
                .slice(0, 50)
                .map(product => (
                  <div
                    key={product.id}
                    className={`gift-product-card ${formData.conditions.products?.includes(product.id) ? 'selected' : ''}`}
                    onClick={() => toggleProduct(product.id)}
                  >
                    {formData.conditions.products?.includes(product.id) && (
                      <span className="gift-selected-badge">✓</span>
                    )}
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="gift-product-image" />
                    ) : (
                      <div className="gift-product-placeholder">
                        {product.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <div className="gift-product-info">
                      <span className="gift-product-name">{product.name}</span>
                      <span className="gift-product-category">{product.category}</span>
                      <span className="gift-product-price">₺{Number(product.sell_price).toLocaleString('tr-TR')}</span>
                    </div>
                  </div>
                ))}
              {products.filter(p => {
                const matchesSearch = !giftProductSearch ||
                  p.name?.toLowerCase().includes(giftProductSearch.toLowerCase()) ||
                  p.barcode?.includes(giftProductSearch);
                const matchesCategory = giftProductCategory === 'all' || p.category === giftProductCategory;
                return matchesSearch && matchesCategory;
              }).length === 0 && (
                <div className="gift-products-empty">Ürün bulunamadı</div>
              )}
            </div>

            {/* Minimum Tutar */}
            <div className="form-group" style={{marginTop: '16px'}}>
              <label>Minimum Alışveriş Tutarı (₺)</label>
              <PriceInput
                value={formData.conditions.min_purchase || 0}
                onChange={(val) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, min_purchase: val }
                })}
                placeholder="0 = Koşul yok"
              />
            </div>

            {/* Minimum Tutar Tipi */}
            {formData.conditions.min_purchase > 0 && (
              <div className="form-group" style={{marginTop: '12px'}}>
                <label>Minimum Tutar Kontrolü</label>
                <div className="min-purchase-type-selector">
                  <button
                    type="button"
                    className={`type-btn ${!formData.conditions.min_purchase_type || formData.conditions.min_purchase_type === 'general' ? 'selected' : ''}`}
                    onClick={() => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, min_purchase_type: 'general' }
                    })}
                  >
                    <span className="type-icon">🛒</span>
                    <span className="type-label">Genel Sepet</span>
                    <span className="type-desc">Tüm sepet toplamı</span>
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${formData.conditions.min_purchase_type === 'product' ? 'selected' : ''}`}
                    onClick={() => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, min_purchase_type: 'product' }
                    })}
                  >
                    <span className="type-icon">📦</span>
                    <span className="type-label">Sadece Ürünler</span>
                    <span className="type-desc">Seçilen ürünler toplamı</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* X Al Y Öde için kategori seçimi */}
        {type === 'buy_x_pay_y' && (
          <div className="form-section">
            <h3>Geçerli Tür ve Kategoriler</h3>
            {renderTypeAndCategorySelector()}
          </div>
        )}

        {/* Minimum Alışveriş */}
        {(type === 'percentage' || type === 'fixed_amount') && (
          <div className="form-section">
            <h3>Koşullar</h3>
            <div className="form-group">
              <label>Minimum Alışveriş Tutarı (₺)</label>
              <PriceInput
                value={formData.conditions.min_purchase || 0}
                onChange={(val) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, min_purchase: val }
                })}
                placeholder="0 = Koşul yok"
              />
            </div>
          </div>
        )}

        {/* Özel Gün Kampanyası */}
        {type === 'special_day' && (
          <div className="form-section">
            <h3>Özel Gün Ayarları</h3>
            <p className="helper-text">Kampanya başlangıç ve bitiş tarihleri arasındaki günlerde geçerli olacaktır.</p>

            <div className="form-group">
              <label>İndirim Türü</label>
              <div className="discount-type-selector">
                <button
                  type="button"
                  className={`discount-type-btn ${formData.rewards.discount_type === 'percentage' ? 'selected' : ''}`}
                  onClick={() => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, discount_type: 'percentage' }
                  })}
                >
                  % Yüzde
                </button>
                <button
                  type="button"
                  className={`discount-type-btn ${formData.rewards.discount_type === 'fixed' ? 'selected' : ''}`}
                  onClick={() => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, discount_type: 'fixed' }
                  })}
                >
                  ₺ Tutar
                </button>
              </div>
            </div>

            {formData.rewards.discount_type === 'percentage' ? (
              <div className="form-group">
                <label>İndirim Yüzdesi (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.rewards.discount_percent}
                  onChange={(e) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, discount_percent: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>
            ) : (
              <div className="form-group">
                <label>İndirim Tutarı (₺)</label>
                <PriceInput
                  value={formData.rewards.discount_amount || 0}
                  onChange={(val) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, discount_amount: val }
                  })}
                />
              </div>
            )}

            <div className="form-group">
              <label>Minimum Alışveriş Tutarı (₺)</label>
              <PriceInput
                value={formData.conditions.min_purchase || 0}
                onChange={(val) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, min_purchase: val }
                })}
                placeholder="0 = Koşul yok"
              />
            </div>

            <div className="repeat-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.rewards.repeat || false}
                  onChange={(e) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, repeat: e.target.checked }
                  })}
                />
                <span>İndirimi tekrarla</span>
              </label>
              <p className="helper-text">
                Aktif olduğunda: Min. tutar her karşılandığında indirim tekrarlanır.
                Örn: 10.000₺'de 1.000₺ indirim → 20.000₺'de 2.000₺, 30.000₺'de 3.000₺...
              </p>
            </div>
          </div>
        )}

        {/* Doğum Günü Kampanyası */}
        {type === 'birthday' && (
          <div className="form-section">
            <h3>Doğum Günü Kampanyası Ayarları</h3>
            <p className="helper-text">Doğum günü olan müşterilere otomatik uygulanır.</p>

            <div className="form-row">
              <div className="form-group">
                <label>Doğum gününden kaç gün önce başlasın?</label>
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={formData.conditions.birthday_days_before}
                  onChange={(e) => setFormData({
                    ...formData,
                    conditions: { ...formData.conditions, birthday_days_before: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>
              <div className="form-group">
                <label>Doğum gününden kaç gün sonra bitsin?</label>
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={formData.conditions.birthday_days_after}
                  onChange={(e) => setFormData({
                    ...formData,
                    conditions: { ...formData.conditions, birthday_days_after: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>
            </div>

            <div className="birthday-preview">
              Örnek: Doğum günü 15 Ocak olan müşteri için kampanya{' '}
              <strong>
                {formData.conditions.birthday_days_before > 0 ? `${formData.conditions.birthday_days_before} gün önce` : 'doğum gününde'} başlar
              </strong>{' '}
              ve{' '}
              <strong>
                {formData.conditions.birthday_days_after > 0 ? `${formData.conditions.birthday_days_after} gün sonra` : 'doğum gününde'} biter
              </strong>.
            </div>

            <div className="form-group" style={{marginTop: '16px'}}>
              <label>İndirim Türü</label>
              <div className="discount-type-selector">
                <button
                  type="button"
                  className={`discount-type-btn ${formData.rewards.discount_type === 'percentage' ? 'selected' : ''}`}
                  onClick={() => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, discount_type: 'percentage' }
                  })}
                >
                  % Yüzde
                </button>
                <button
                  type="button"
                  className={`discount-type-btn ${formData.rewards.discount_type === 'fixed' ? 'selected' : ''}`}
                  onClick={() => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, discount_type: 'fixed' }
                  })}
                >
                  ₺ Tutar
                </button>
              </div>
            </div>

            {formData.rewards.discount_type === 'percentage' ? (
              <div className="form-group">
                <label>İndirim Yüzdesi (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.rewards.discount_percent}
                  onChange={(e) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, discount_percent: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>
            ) : (
              <div className="form-group">
                <label>İndirim Tutarı (₺)</label>
                <PriceInput
                  value={formData.rewards.discount_amount || 0}
                  onChange={(val) => setFormData({
                    ...formData,
                    rewards: { ...formData.rewards, discount_amount: val }
                  })}
                />
              </div>
            )}

            <div className="form-group">
              <label>Minimum Alışveriş Tutarı (₺)</label>
              <PriceInput
                value={formData.conditions.min_purchase || 0}
                onChange={(val) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, min_purchase: val }
                })}
                placeholder="0 = Koşul yok"
              />
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="campaigns-container">
      <header className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={18} /> Ana Sayfa
          </button>
          <h1>Kampanyalar & Hediye Kartları</h1>
        </div>
        <div className="header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-actions">
          <button className="action-header-btn log-btn" onClick={() => setShowLogModal(true)}>
            <ScrollText size={18} /> İşlem Geçmişi
          </button>
          {canEditCampaigns() && (
            activeTab === 'campaigns' ? (
              <button className="action-header-btn" onClick={() => { resetForm(); setShowModal(true); }}>
                <Plus size={18} /> Yeni Kampanya
              </button>
            ) : (
              <button className="action-header-btn gift-card-btn" onClick={() => setShowGiftCardModal(true)}>
                <Plus size={18} /> Yeni Hediye Kartı
              </button>
            )
          )}
        </div>
      </header>

      {/* Tab Menüsü */}
      <div className="tab-menu">
        <button
          className={`tab-btn ${activeTab === 'campaigns' ? 'active' : ''}`}
          onClick={() => setActiveTab('campaigns')}
        >
          Kampanyalar
        </button>
        <button
          className={`tab-btn ${activeTab === 'giftcards' ? 'active' : ''}`}
          onClick={() => setActiveTab('giftcards')}
        >
          Hediye Kartları
        </button>
        <button className="help-btn-tab" title="Yardım" onClick={() => setShowHelpModal(true)}>?</button>
      </div>

      {activeTab === 'campaigns' ? (
      <div className="campaigns-content">
        {/* Admin için Mağaza Filtresi */}
        {user?.role === 'admin' && (
          <div className="store-filter-row">
            <div
              className={`store-filter-card ${storeTypeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStoreTypeFilter('all')}
            >
              <Store size={24} />
              <span>Tüm Mağazalar</span>
            </div>
            <div
              className={`store-filter-card gold ${storeTypeFilter === 'gold' ? 'active' : ''}`}
              onClick={() => setStoreTypeFilter('gold')}
            >
              <Coins size={24} />
              <span>Altın Mağazası</span>
            </div>
            <div
              className={`store-filter-card silver ${storeTypeFilter === 'silver' ? 'active' : ''}`}
              onClick={() => setStoreTypeFilter('silver')}
            >
              <CircleDot size={24} />
              <span>Gümüş Mağazaları</span>
            </div>
          </div>
        )}

        {/* Kasiyer için mağaza bilgisi */}
        {userStoreType && (
          <div className="store-info-banner">
            {userStoreType === 'gold' ? <Coins size={20} /> : <CircleDot size={20} />}
            <span>{userStoreType === 'gold' ? 'Altın Mağazası' : 'Gümüş Mağazaları'} Kampanyaları</span>
          </div>
        )}

        {/* Filters */}
        <div className="filters-row">
          <div className="search-box">
            <span className="search-icon">
              <Search size={18} />
            </span>
            <input
              type="text"
              placeholder="Kampanya ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              Tümü
            </button>
            <button
              className={`filter-btn ${filterStatus === 'active' ? 'active' : ''}`}
              onClick={() => setFilterStatus('active')}
            >
              Aktif
            </button>
            <button
              className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
              onClick={() => setFilterStatus('pending')}
            >
              Beklemede
            </button>
            <button
              className={`filter-btn ${filterStatus === 'inactive' ? 'active' : ''}`}
              onClick={() => setFilterStatus('inactive')}
            >
              Pasif
            </button>
          </div>
        </div>

        {/* Campaigns List */}
        {loading ? (
          <div className="loading">Yükleniyor...</div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="empty-state">
            <Tag size={48} style={{ color: '#9ca3af' }} />
            <p>Kampanya bulunamadı</p>
          </div>
        ) : (
          <div className="campaigns-grid">
            {filteredCampaigns.map(campaign => {
              const status = getCampaignStatus(campaign);
              return (
                <div key={campaign.id} className={`campaign-card ${!campaign.active ? 'inactive' : ''}`}>
                  <div className="campaign-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span className={`status-badge ${status.class}`}>{status.label}</span>
                      <span className="campaign-type-badge">
                        {CAMPAIGN_TYPES[campaign.campaign_type]?.label || campaign.campaign_type}
                      </span>
                      {campaign.ecommerce_active === 1 && (
                        <span className="campaign-type-badge" style={{
                          background: '#f5f3ff',
                          color: '#7c3aed',
                          border: '1px solid #ddd6fe',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <ShoppingCart size={12} />
                          Shopify
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="campaign-title">
                    <h3>{campaign.name}</h3>
                    {campaign.description && <p>{campaign.description}</p>}
                  </div>

                  <div className="campaign-summary">
                    {getCampaignSummary(campaign)}
                  </div>

                  <div className="campaign-dates">
                    <div className="date-item">
                      <span className="date-label">Başlangıç</span>
                      <span className="date-value">{formatDateTR(campaign.start_date)}</span>
                    </div>
                    <div className="date-separator">→</div>
                    <div className="date-item">
                      <span className="date-label">Bitiş</span>
                      <span className="date-value">{formatDateTR(campaign.end_date)}</span>
                    </div>
                  </div>

                  <div className="campaign-actions">
                    <button
                      className={`toggle-btn ${campaign.active ? 'active' : ''}`}
                      onClick={() => handleToggleActive(campaign)}
                    >
                      {campaign.active ? '● Aktif' : '○ Pasif'}
                    </button>
                    <button className="btn-icon history" onClick={() => fetchCampaignUsage(campaign)} title="Kullanım Geçmişi">
                      <Clock size={16} />
                    </button>
                    {canEditCampaigns() && (
                      <>
                        <button className="btn-icon edit" onClick={() => openEditModal(campaign)} title="Düzenle">
                          <Pencil size={16} />
                        </button>
                        <button className="btn-icon delete" onClick={() => handleDelete(campaign)} title="Sil">
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      ) : activeTab === 'giftcards' ? (
      /* Hediye Kartları Tab İçeriği */
      <div className="giftcards-content-minimal">
        {/* Header with stats inline */}
        <div className="giftcards-header-row">
          <div className="giftcards-mini-stats">
            <span className="mini-stat"><strong>{giftCards.filter(c => c.status === 'active').length}</strong> aktif</span>
            <span className="mini-stat-divider">•</span>
            {giftCards.filter(c => c.status === 'active' && (!c.amount_unit || c.amount_unit === 'TL')).length > 0 && (
              <span className="mini-stat total-balance">₺{giftCards.filter(c => c.status === 'active' && (!c.amount_unit || c.amount_unit === 'TL')).reduce((sum, c) => sum + Number(c.remaining_amount), 0).toLocaleString('tr-TR')}</span>
            )}
            {giftCards.filter(c => c.status === 'active' && c.amount_unit === 'USD').length > 0 && (
              <>
                <span className="mini-stat-divider">•</span>
                <span className="mini-stat total-balance">${giftCards.filter(c => c.status === 'active' && c.amount_unit === 'USD').reduce((sum, c) => sum + Number(c.remaining_amount), 0).toLocaleString('tr-TR')}</span>
              </>
            )}
            {giftCards.filter(c => c.status === 'active' && c.amount_unit === 'EUR').length > 0 && (
              <>
                <span className="mini-stat-divider">•</span>
                <span className="mini-stat total-balance">€{giftCards.filter(c => c.status === 'active' && c.amount_unit === 'EUR').reduce((sum, c) => sum + Number(c.remaining_amount), 0).toLocaleString('tr-TR')}</span>
              </>
            )}
          </div>
          <div className="giftcard-header-buttons">
            {canEditCampaigns() && (
              <button className="btn-excel" onClick={() => setShowExcelModal(true)}>
                <FileSpreadsheet size={16} /> Excel
              </button>
            )}
            {canEditCampaigns() && <button className="btn-add-card" onClick={() => setShowGiftCardModal(true)}>+ Yeni Kart</button>}
          </div>
        </div>

        {/* Compact Table */}
        <div className="giftcards-table-compact">
          {giftCards.length === 0 ? (
            <div className="empty-minimal">Henüz hediye kartı yok</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>Müşteri</th>
                  <th>Bakiye</th>
                  <th>Durum</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {giftCards.map(card => (
                  <tr key={card.id} className={card.status !== 'active' ? 'inactive' : ''}>
                    <td><code>{card.code}</code></td>
                    <td>{card.customer_name || <span style={{ color: '#94a3b8' }}>-</span>}</td>
                    <td className="balance-col">
                      <span className="amount">
                        {card.amount_unit === 'USD' ? '$' : card.amount_unit === 'EUR' ? '€' : '₺'}
                        {Number(card.remaining_amount).toLocaleString('tr-TR')}
                      </span>
                      {Number(card.remaining_amount) < Number(card.amount) && (
                        <span className="original">
                          / {card.amount_unit === 'USD' ? '$' : card.amount_unit === 'EUR' ? '€' : '₺'}
                          {Number(card.amount).toLocaleString('tr-TR')}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`status-tag ${card.status}`}>
                        {card.status === 'active' ? 'Aktif' : card.status === 'used' ? 'Kullanıldı' : card.status === 'expired' ? 'Süresi Doldu' : 'İptal'}
                      </span>
                    </td>
                    <td className="actions-col">
                      <button className="btn-print" onClick={() => printGiftCard(card)} title="Fiş Yazdır" style={{ overflow: 'visible' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Printer size={14} stroke="#334155" strokeWidth={2} fill="none" /></span></button>
                      {canEditCampaigns() && (
                        <>
                          {card.status === 'active' && (
                            <button className="btn-edit" onClick={() => openEditGiftCardModal(card)} title="Düzenle" style={{ overflow: 'visible' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Pencil size={14} stroke="#334155" strokeWidth={2} fill="none" /></span></button>
                          )}
                          {card.status === 'active' && (
                            <button className="btn-x" onClick={() => cancelGiftCard(card)} title="İptal Et" style={{ overflow: 'visible' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><XCircle size={14} stroke="#ef4444" strokeWidth={2} fill="none" /></span></button>
                          )}
                          <button className="btn-del" onClick={() => deleteGiftCard(card)} title="Sil" style={{ overflow: 'visible' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={14} stroke="#ef4444" strokeWidth={2} fill="none" /></span></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      ) : (
      /* Hurda Borçları Tab İçeriği */
      <div className="giftcards-content-minimal scrap-debts-content">
        {/* Header with stats inline */}
        <div className="giftcards-header-row">
          <div className="giftcards-mini-stats">
            <span className="mini-stat"><strong>{scrapDebtCards.filter(c => c.status === 'active').length}</strong> aktif borç</span>
            <span className="mini-stat-divider">•</span>
            <span className="mini-stat total-balance">₺{scrapDebtCards.filter(c => c.status === 'active').reduce((sum, c) => sum + Number(c.remaining_amount), 0).toLocaleString('tr-TR')} toplam</span>
          </div>
        </div>

        {/* Compact Table */}
        <div className="giftcards-table-compact">
          {scrapDebtCards.length === 0 ? (
            <div className="empty-minimal">Henüz hurda borcu yok. Hurda alımında açık hesap seçildiğinde buraya düşecektir.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>Müşteri</th>
                  <th>Borç Tipi</th>
                  <th>Bakiye</th>
                  <th>Durum</th>
                  <th>Tarih</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {scrapDebtCards.map(card => (
                  <tr key={card.id} className={card.status !== 'active' ? 'inactive' : ''}>
                    <td><code style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>{card.code}</code></td>
                    <td>{card.customer_name || '-'}</td>
                    <td>
                      <span className={`debt-type-badge ${card.debt_type?.toLowerCase() || 'tl'}`}>
                        {card.debt_type === 'GRAM' ? '🥇 Altın' : card.debt_type === 'GUMUS' ? '🥈 Gümüş' : '💵 TL'}
                      </span>
                    </td>
                    <td className="balance-col">
                      {/* Gram bazlı kartlar için gram göster, TL bazlı için TL göster - debt_type kontrolü */}
                      {card.debt_type === 'GRAM' || card.debt_type === 'GUMUS' ? (
                        <>
                          <span className="amount">{Number(card.remaining_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr</span>
                          {Number(card.remaining_amount) < Number(card.amount) && (
                            <span className="original">/ {Number(card.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr</span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="amount">₺{Number(card.remaining_amount).toLocaleString('tr-TR')}</span>
                          {Number(card.remaining_amount) < Number(card.amount) && (
                            <span className="original">/ ₺{Number(card.amount).toLocaleString('tr-TR')}</span>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      <span className={`status-tag ${card.status}`}>
                        {card.status === 'active' ? 'Aktif' : card.status === 'used' ? 'Kullanıldı' : card.status === 'expired' ? 'Süresi Doldu' : 'İptal'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                      {new Date(card.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="actions-col">
                      <button className="btn-print" onClick={() => printGiftCard(card)} title="Fiş Yazdır" style={{ overflow: 'visible' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Printer size={14} stroke="#334155" strokeWidth={2} fill="none" /></span></button>
                      {canEditCampaigns() && (
                        <>
                          {card.status === 'active' && (
                            <button className="btn-edit" onClick={() => openEditScrapDebtModal(card)} title="Düzenle" style={{ overflow: 'visible' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Pencil size={14} stroke="#334155" strokeWidth={2} fill="none" /></span></button>
                          )}
                          {card.status === 'active' && (
                            <button className="btn-x" onClick={() => cancelGiftCard(card)} title="İptal Et" style={{ overflow: 'visible' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><XCircle size={14} stroke="#ef4444" strokeWidth={2} fill="none" /></span></button>
                          )}
                          <button className="btn-del" onClick={() => deleteGiftCard(card)} title="Sil" style={{ overflow: 'visible' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={14} stroke="#ef4444" strokeWidth={2} fill="none" /></span></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      {/* Excel Import Modal */}
      {showExcelModal && (
        <div className="modal-overlay">
          <div className="modal excel-import-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FileSpreadsheet size={20} style={{ marginRight: '8px' }} /> Excel ile Hediye Kartı</h2>
              <button className="close-btn" onClick={closeExcelModal}>×</button>
            </div>
            <div className="modal-body">
              {excelProcessing ? (
                <div className="excel-processing">
                  <div className="spinner"></div>
                  <p>İşleniyor...</p>
                </div>
              ) : excelImportStep === 'select' ? (
                // Adım 1: Seçenekler
                <div className="excel-options">
                  <p className="excel-description">
                    Excel dosyası ile toplu bakiye oluşturabilirsiniz.
                  </p>
                  <div className="excel-option-buttons">
                    <label className="excel-option-btn upload">
                      <Upload size={24} />
                      <span>İçe Aktar</span>
                      <small>Excel dosyası seç</small>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleExcelFileUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button className="excel-option-btn template" onClick={downloadExcelTemplate}>
                      <Download size={24} />
                      <span>Örnek Şablon</span>
                      <small>Excel şablonu indir</small>
                    </button>
                  </div>
                  <div className="excel-format-info">
                    <h4>Excel Formatı:</h4>
                    <table className="format-table">
                      <thead>
                        <tr>
                          <th>Sütun</th>
                          <th>Açıklama</th>
                          <th>Örnek</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td>1</td><td>Müşteri Ad Soyad</td><td>Ahmet Yılmaz</td></tr>
                        <tr><td>2</td><td>Telefon No</td><td>05551234567</td></tr>
                        <tr><td>3</td><td>Miktar</td><td>100</td></tr>
                        <tr><td>4</td><td>Para Birimi</td><td>TL, USD, EUR</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : excelImportStep === 'preview' ? (
                // Adım 2: Önizleme (tüm müşteriler eşleşti)
                <div className="excel-preview">
                  <div className="preview-summary success">
                    <CheckCircle size={20} />
                    <span><strong>{matchedCustomers.length}</strong> müşteri eşleşti ve bakiyeler oluşturulacak</span>
                  </div>
                  <div className="preview-list">
                    {matchedCustomers.slice(0, 10).map((item, i) => (
                      <div key={i} className="preview-item matched">
                        <span className="customer-name">{item.customer.name}</span>
                        <span className="amount">{item.currency === 'TL' ? '₺' : item.currency === 'USD' ? '$' : '€'}{item.amount}</span>
                      </div>
                    ))}
                    {matchedCustomers.length > 10 && (
                      <div className="preview-more">ve {matchedCustomers.length - 10} kayıt daha...</div>
                    )}
                  </div>
                  <div className="preview-actions">
                    <button className="btn-cancel" onClick={closeExcelModal}>İptal</button>
                    <button className="btn-save" onClick={() => completeExcelImport(false)}>
                      {matchedCustomers.length} Kart Oluştur
                    </button>
                  </div>
                </div>
              ) : excelImportStep === 'confirm_new_customers' ? (
                // Adım 3: Eşleşmeyen müşteriler için onay
                <div className="excel-confirm-new">
                  {matchedCustomers.length > 0 && (
                    <div className="preview-summary success">
                      <CheckCircle size={20} />
                      <span><strong>{matchedCustomers.length}</strong> müşteri eşleşti</span>
                    </div>
                  )}
                  <div className="preview-summary warning">
                    <UserPlus size={20} />
                    <span><strong>{unmatchedCustomers.length}</strong> müşteri sistemde bulunamadı</span>
                  </div>

                  <h4>Eşleşmeyen Müşteriler:</h4>
                  <div className="unmatched-list">
                    {unmatchedCustomers.map((item, i) => (
                      <div key={i} className="unmatched-item">
                        <div className="unmatched-info">
                          <span className="name">{item.name}</span>
                          {item.phone && <span className="phone">{item.phone}</span>}
                        </div>
                        <span className="amount">{item.currency === 'TL' ? '₺' : item.currency === 'USD' ? '$' : '€'}{item.amount}</span>
                      </div>
                    ))}
                  </div>

                  <div className="confirm-question">
                    <p>Bu müşterileri yeni kayıt olarak eklemek ister misiniz?</p>
                  </div>

                  <div className="preview-actions three-buttons">
                    <button className="btn-cancel" onClick={closeExcelModal}>İptal</button>
                    <button className="btn-secondary" onClick={() => completeExcelImport(false)}>
                      Sadece Eşleşenler ({matchedCustomers.length})
                    </button>
                    <button className="btn-save" onClick={() => completeExcelImport(true)}>
                      <UserPlus size={16} /> Tümünü Ekle ({matchedCustomers.length + unmatchedCustomers.length})
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Hediye Kartı Oluşturma Modal - Compact */}
      {showGiftCardModal && (
        <div className={`modal-overlay ${closingModal === 'giftcard' ? 'closing' : ''}`}>
          <div className="modal small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yeni Hediye Kartı</h2>
              <button className="close-btn" onClick={() => handleCloseModal('giftcard', setShowGiftCardModal, () => {
                setGiftCardForm({ code: '', amount: '', expires_at: '', card_type: 'random', customer_id: null, customer_name: '', currency: 'TL' });
                setGiftCardCustomerSearch('');
                setGiftCardCustomerResults([]);
              })}>×</button>
            </div>
            <div className="modal-body compact">
              {/* Kart Tipi Seçici */}
              <div className="form-group">
                <label>Kart Tipi</label>
                <div className="card-type-selector">
                  <button
                    type="button"
                    className={`card-type-btn ${giftCardForm.card_type === 'random' ? 'selected' : ''}`}
                    onClick={() => setGiftCardForm({...giftCardForm, card_type: 'random', customer_id: null, customer_name: ''})}
                  >
                    Rastgele
                  </button>
                  <button
                    type="button"
                    className={`card-type-btn ${giftCardForm.card_type === 'customer_specific' ? 'selected' : ''}`}
                    onClick={() => setGiftCardForm({...giftCardForm, card_type: 'customer_specific'})}
                  >
                    Müşteriye Özel
                  </button>
                </div>
              </div>

              {/* Müşteri Seçimi (Müşteriye Özel seçilirse) */}
              {giftCardForm.card_type === 'customer_specific' && (
                <div className="form-group">
                  <label>Müşteri</label>
                  {giftCardForm.customer_id ? (
                    <div className="selected-customer-display">
                      <span className="customer-name">{giftCardForm.customer_name}</span>
                      <button type="button" className="btn-clear-customer" onClick={clearGiftCardCustomer}>×</button>
                    </div>
                  ) : (
                    <div className="customer-search-wrapper">
                      <input
                        type="text"
                        placeholder="Müşteri adı veya telefon ile ara..."
                        value={giftCardCustomerSearch}
                        onChange={(e) => searchGiftCardCustomer(e.target.value)}
                        onFocus={() => giftCardCustomerResults.length > 0 && setShowGiftCardCustomerDropdown(true)}
                      />
                      {showGiftCardCustomerDropdown && giftCardCustomerResults.length > 0 && (
                        <div className="customer-dropdown">
                          {giftCardCustomerResults.map(customer => (
                            <div
                              key={customer.id}
                              className="customer-dropdown-item"
                              onClick={() => selectGiftCardCustomer(customer)}
                            >
                              <span className="customer-name">{customer.name}</span>
                              {customer.phone && <span className="customer-phone">{customer.phone}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Para Birimi ve Tutar */}
              <div className="form-row-inline">
                <div className="form-group">
                  <label>Para Birimi</label>
                  <div className="currency-selector">
                    <button
                      type="button"
                      className={`currency-btn ${giftCardForm.currency === 'TL' ? 'selected' : ''}`}
                      onClick={() => setGiftCardForm({...giftCardForm, currency: 'TL'})}
                    >
                      ₺ TL
                    </button>
                    <button
                      type="button"
                      className={`currency-btn ${giftCardForm.currency === 'USD' ? 'selected' : ''}`}
                      onClick={() => setGiftCardForm({...giftCardForm, currency: 'USD'})}
                    >
                      $ USD
                    </button>
                    <button
                      type="button"
                      className={`currency-btn ${giftCardForm.currency === 'EUR' ? 'selected' : ''}`}
                      onClick={() => setGiftCardForm({...giftCardForm, currency: 'EUR'})}
                    >
                      € EUR
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Tutar ({giftCardForm.currency === 'TL' ? '₺' : giftCardForm.currency === 'USD' ? '$' : '€'})</label>
                  <PriceInput
                    value={parseFloat(giftCardForm.amount) || 0}
                    onChange={(val) => setGiftCardForm({...giftCardForm, amount: val})}
                    placeholder="500"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Son Kullanma</label>
                <input
                  type="date"
                  value={giftCardForm.expires_at}
                  onChange={(e) => setGiftCardForm({...giftCardForm, expires_at: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Kod (opsiyonel)</label>
                <input
                  type="text"
                  placeholder="Otomatik oluşturulur..."
                  value={giftCardForm.code}
                  onChange={(e) => setGiftCardForm({...giftCardForm, code: e.target.value.toUpperCase()})}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => handleCloseModal('giftcard', setShowGiftCardModal, () => {
                setGiftCardForm({ code: '', amount: '', expires_at: '', card_type: 'random', customer_id: null, customer_name: '', currency: 'TL' });
                setGiftCardCustomerSearch('');
                setGiftCardCustomerResults([]);
              })}>İptal</button>
              <button className="btn-save" onClick={createGiftCard}>Oluştur</button>
            </div>
          </div>
        </div>
      )}

      {/* Hediye Kartı Düzenleme Modal */}
      {showEditGiftCardModal && editingGiftCard && (
        <div className="modal-overlay">
          <div className="modal small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Hediye Kartı Düzenle</h2>
              <span className="modal-subtitle">{editingGiftCard.code}</span>
              <button className="close-btn" onClick={() => setShowEditGiftCardModal(false)}>×</button>
            </div>
            <div className="modal-body compact">
              {/* Para Birimi */}
              <div className="form-group">
                <label>Para Birimi</label>
                <div className="currency-selector">
                  <button
                    type="button"
                    className={`currency-btn ${editGiftCardForm.currency === 'TL' ? 'selected' : ''}`}
                    onClick={() => setEditGiftCardForm({...editGiftCardForm, currency: 'TL'})}
                  >
                    ₺ TL
                  </button>
                  <button
                    type="button"
                    className={`currency-btn ${editGiftCardForm.currency === 'USD' ? 'selected' : ''}`}
                    onClick={() => setEditGiftCardForm({...editGiftCardForm, currency: 'USD'})}
                  >
                    $ USD
                  </button>
                  <button
                    type="button"
                    className={`currency-btn ${editGiftCardForm.currency === 'EUR' ? 'selected' : ''}`}
                    onClick={() => setEditGiftCardForm({...editGiftCardForm, currency: 'EUR'})}
                  >
                    € EUR
                  </button>
                </div>
              </div>

              {/* Tutar ve Kalan Bakiye */}
              <div className="form-row-inline">
                <div className="form-group">
                  <label>Toplam Tutar</label>
                  <PriceInput
                    value={parseFloat(editGiftCardForm.amount) || 0}
                    onChange={(val) => setEditGiftCardForm({...editGiftCardForm, amount: val})}
                  />
                </div>
                <div className="form-group">
                  <label>Kalan Bakiye</label>
                  <PriceInput
                    value={parseFloat(editGiftCardForm.remaining_amount) || 0}
                    onChange={(val) => setEditGiftCardForm({...editGiftCardForm, remaining_amount: val})}
                  />
                </div>
              </div>

              {/* Son Kullanma */}
              <div className="form-group">
                <label>Son Kullanma Tarihi</label>
                <input
                  type="date"
                  value={editGiftCardForm.expires_at}
                  onChange={(e) => setEditGiftCardForm({...editGiftCardForm, expires_at: e.target.value})}
                />
              </div>

              {/* Müşteri */}
              <div className="form-group">
                <label>Müşteri (opsiyonel)</label>
                {editGiftCardForm.customer_id ? (
                  <div className="selected-customer-display">
                    <span className="customer-name">{editGiftCardForm.customer_name}</span>
                    <button type="button" className="btn-clear-customer" onClick={() => setEditGiftCardForm({...editGiftCardForm, customer_id: null, customer_name: ''})}>×</button>
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="Müşteri adı girin..."
                    value={editGiftCardForm.customer_name}
                    onChange={(e) => setEditGiftCardForm({...editGiftCardForm, customer_name: e.target.value})}
                  />
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowEditGiftCardModal(false)}>İptal</button>
              <button className="btn-save" onClick={updateGiftCard}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Hurda Borcu Düzenleme Modal */}
      {showEditScrapDebtModal && editingScrapDebt && (
        <div className="modal-overlay">
          <div className="modal small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Hurda Borcu Düzenle</h2>
              <span className="modal-subtitle">{editingScrapDebt.code}</span>
              <button className="close-btn" onClick={() => setShowEditScrapDebtModal(false)}>×</button>
            </div>
            <div className="modal-body compact">
              {/* Borç Tipi */}
              <div className="form-group">
                <label>Borç Tipi</label>
                <div className="currency-selector">
                  <button
                    type="button"
                    className={`currency-btn ${editScrapDebtForm.debt_type === 'TL' ? 'selected' : ''}`}
                    onClick={() => setEditScrapDebtForm({...editScrapDebtForm, debt_type: 'TL'})}
                  >
                    💵 TL
                  </button>
                  <button
                    type="button"
                    className={`currency-btn ${editScrapDebtForm.debt_type === 'GRAM' ? 'selected' : ''}`}
                    onClick={() => setEditScrapDebtForm({...editScrapDebtForm, debt_type: 'GRAM'})}
                  >
                    🥇 Altın
                  </button>
                  <button
                    type="button"
                    className={`currency-btn ${editScrapDebtForm.debt_type === 'GUMUS' ? 'selected' : ''}`}
                    onClick={() => setEditScrapDebtForm({...editScrapDebtForm, debt_type: 'GUMUS'})}
                  >
                    🥈 Gümüş
                  </button>
                </div>
              </div>

              {/* Tutar ve Kalan Bakiye */}
              <div className="form-row-inline">
                <div className="form-group">
                  <label>Toplam {editScrapDebtForm.debt_type === 'TL' ? 'Tutar (₺)' : 'Gram'}</label>
                  {editScrapDebtForm.debt_type === 'TL' ? (
                    <PriceInput
                      value={parseFloat(editScrapDebtForm.amount) || 0}
                      onChange={(val) => setEditScrapDebtForm({...editScrapDebtForm, amount: val})}
                    />
                  ) : (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editScrapDebtForm.amount}
                      onChange={(e) => setEditScrapDebtForm({...editScrapDebtForm, amount: e.target.value})}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Kalan {editScrapDebtForm.debt_type === 'TL' ? 'Bakiye (₺)' : 'Gram'}</label>
                  {editScrapDebtForm.debt_type === 'TL' ? (
                    <PriceInput
                      value={parseFloat(editScrapDebtForm.remaining_amount) || 0}
                      onChange={(val) => setEditScrapDebtForm({...editScrapDebtForm, remaining_amount: val})}
                    />
                  ) : (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editScrapDebtForm.remaining_amount}
                      onChange={(e) => setEditScrapDebtForm({...editScrapDebtForm, remaining_amount: e.target.value})}
                    />
                  )}
                </div>
              </div>

              {/* Müşteri */}
              <div className="form-group">
                <label>Müşteri</label>
                <input
                  type="text"
                  placeholder="Müşteri adı..."
                  value={editScrapDebtForm.customer_name}
                  onChange={(e) => setEditScrapDebtForm({...editScrapDebtForm, customer_name: e.target.value})}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowEditScrapDebtModal(false)}>İptal</button>
              <button className="btn-save" onClick={updateScrapDebt}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Kampanya Kullanım Geçmişi Modal */}
      {showUsageModal && (
        <div className={`modal-overlay ${closingModal === 'usage' ? 'closing' : ''}`}>
          <div className="modal xlarge" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{showUsageModal.name}</h2>
                <span className="modal-subtitle">Kullanım Geçmişi</span>
              </div>
              <button className="close-btn" onClick={closeUsageModal}>×</button>
            </div>
            <div className="modal-body">
              {usageLoading ? (
                <div className="loading">Yükleniyor...</div>
              ) : (
                <>
                  {/* İstatistikler */}
                  {campaignUsageStats && (
                    <div className="usage-stats-row">
                      <div className="usage-stat">
                        <span className="usage-stat-value">{campaignUsageStats.total_usage || 0}</span>
                        <span className="usage-stat-label">Kullanım</span>
                      </div>
                      <div className="usage-stat">
                        <span className="usage-stat-value">{campaignUsageStats.unique_customers || 0}</span>
                        <span className="usage-stat-label">Müşteri</span>
                      </div>
                      <div className="usage-stat">
                        <span className="usage-stat-value">₺{Number(campaignUsageStats.total_discount || 0).toLocaleString()}</span>
                        <span className="usage-stat-label">Toplam İndirim</span>
                      </div>
                      <div className="usage-stat">
                        <span className="usage-stat-value">₺{Number(campaignUsageStats.total_revenue || 0).toLocaleString()}</span>
                        <span className="usage-stat-label">Toplam Gelir</span>
                      </div>
                    </div>
                  )}

                  {/* Kullanım Listesi */}
                  {campaignUsageHistory.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">G</div>
                      <p>Bu kampanya henüz kullanılmamış</p>
                    </div>
                  ) : (
                    <div className="history-table-container">
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>Tarih</th>
                            <th>Müşteri</th>
                            <th>Personel</th>
                            <th>Ürünler</th>
                            <th>Tutar</th>
                            <th>İndirim</th>
                            <th>Ödenen</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {campaignUsageHistory.map(usage => {
                            const usageProducts = getUsageProducts(usage);
                            return (
                              <tr key={usage.id}>
                                <td className="date-cell">
                                  {new Date(usage.created_at).toLocaleDateString('tr-TR')}
                                  <span className="time">{new Date(usage.created_at).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})}</span>
                                </td>
                                <td>
                                  <span className="customer-name">{usage.customer_name || 'Misafir'}</span>
                                  {usage.customer_phone && <span className="customer-phone">{usage.customer_phone}</span>}
                                </td>
                                <td>{usage.staff_name || '-'}</td>
                                <td className="products-cell">
                                  <span className="product-count">{usageProducts.length} ürün</span>
                                </td>
                                <td className="amount-cell">₺{Number(usage.original_total).toLocaleString()}</td>
                                <td className="discount-cell">-₺{Number(usage.discount_amount).toLocaleString()}</td>
                                <td className="final-cell">₺{Number(usage.final_total).toLocaleString()}</td>
                                <td>
                                  <button
                                    className="btn-detail"
                                    onClick={() => setShowUsageDetail(usage)}
                                    title="Detay"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                      <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeUsageModal}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Kullanım Detay Modal */}
      {showUsageDetail && (
        <div className={`modal-overlay ${closingModal === 'usageDetail' ? 'closing' : ''}`}>
          <div className="modal medium" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Satış Detayı</h2>
              <button className="close-btn" onClick={() => { setClosingModal('usageDetail'); setTimeout(() => { setShowUsageDetail(null); setClosingModal(null); }, 250); }}>×</button>
            </div>
            <div className="modal-body">
              <div className="usage-detail-section">
                <h4>İşlem Bilgisi</h4>
                <div className="detail-row">
                  <span className="label">Tarih:</span>
                  <span className="value">{new Date(showUsageDetail.created_at).toLocaleString('tr-TR')}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Müşteri:</span>
                  <span className="value">{showUsageDetail.customer_name || 'Misafir Müşteri'}</span>
                </div>
                {showUsageDetail.customer_phone && (
                  <div className="detail-row">
                    <span className="label">Telefon:</span>
                    <span className="value">{showUsageDetail.customer_phone}</span>
                  </div>
                )}
                {showUsageDetail.staff_name && (
                  <div className="detail-row">
                    <span className="label">Personel:</span>
                    <span className="value">{showUsageDetail.staff_name}</span>
                  </div>
                )}
              </div>

              <div className="usage-detail-section">
                <h4>Satın Alınan Ürünler</h4>
                <div className="products-list">
                  {getUsageProducts(showUsageDetail).map((product, index) => (
                    <div key={index} className="product-item">
                      <span className="product-name">{product.name || product.product_name}</span>
                      <span className="product-qty">x{product.quantity}</span>
                      <span className="product-price">₺{Number(product.price || product.unit_price || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="usage-detail-section summary">
                <div className="summary-row">
                  <span>Ara Toplam:</span>
                  <span>₺{Number(showUsageDetail.original_total).toLocaleString()}</span>
                </div>
                <div className="summary-row discount">
                  <span>İndirim:</span>
                  <span>-₺{Number(showUsageDetail.discount_amount).toLocaleString()}</span>
                </div>
                {showUsageDetail.discount_detail && (
                  <div className="discount-detail">
                    {showUsageDetail.discount_detail}
                  </div>
                )}
                <div className="summary-row total">
                  <span>Ödenen Tutar:</span>
                  <span>₺{Number(showUsageDetail.final_total).toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowUsageDetail(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className={`modal-overlay ${closingModal === 'main' ? 'closing' : ''}`}>
          <div className="modal xlarge" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya'}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('main', setShowModal)}>×</button>
            </div>
            <div className="modal-body">
              {/* Temel Bilgiler */}
              <div className="form-section">
                <h3>Kampanya Bilgileri</h3>
                <div className="form-group">
                  <label>Kampanya Adı *</label>
                  <input
                    type="text"
                    placeholder="Örn: Yılsonu Kampanyası"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Açıklama</label>
                  <textarea
                    placeholder="Kampanya detayları..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  ></textarea>
                </div>
                <div className="form-group">
                  <label>Geçerli Mağaza *</label>
                  <div className="store-type-selector">
                    <button
                      type="button"
                      className={`store-type-btn ${formData.store_type === 'all' ? 'selected' : ''}`}
                      onClick={() => setFormData({...formData, store_type: 'all'})}
                    >
                      <Store size={18} />
                      <span>Tüm Mağazalar</span>
                    </button>
                    <button
                      type="button"
                      className={`store-type-btn gold ${formData.store_type === 'gold' ? 'selected' : ''}`}
                      onClick={() => setFormData({...formData, store_type: 'gold'})}
                    >
                      <Coins size={18} />
                      <span>Altın Mağazaları</span>
                    </button>
                    <button
                      type="button"
                      className={`store-type-btn silver ${formData.store_type === 'silver' ? 'selected' : ''}`}
                      onClick={() => setFormData({...formData, store_type: 'silver'})}
                    >
                      <CircleDot size={18} />
                      <span>Gümüş Mağazaları</span>
                    </button>
                  </div>
                  {/* Bireysel mağaza seçimi */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    {stores.filter(s => s.id !== 4).map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setFormData({...formData, store_type: `store_${s.id}`})}
                        style={{
                          padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600,
                          cursor: 'pointer', transition: 'all 0.15s',
                          border: formData.store_type === `store_${s.id}` ? '2px solid #0f766e' : '1px solid #e2e8f0',
                          background: formData.store_type === `store_${s.id}` ? '#f0fdfa' : '#fff',
                          color: formData.store_type === `store_${s.id}` ? '#0f766e' : '#64748b'
                        }}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Kampanya Türü */}
              <div className="form-section">
                <h3>Kampanya Türü</h3>
                <div className="campaign-types-grid">
                  {Object.entries(CAMPAIGN_TYPES).map(([key, value]) => (
                    <button
                      key={key}
                      type="button"
                      className={`campaign-type-btn ${formData.campaign_type === key ? 'selected' : ''}`}
                      onClick={() => setFormData({...formData, campaign_type: key})}
                    >
                      <span className="type-label">{value.label}</span>
                      <span className="type-desc">{value.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Türe Özel Alanlar */}
              {renderTypeSpecificFields()}

              {/* Tarihler */}
              <div className="form-section">
                <h3>Geçerlilik Tarihleri</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Başlangıç Tarihi *</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Bitiş Tarihi *</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Ek Ayarlar */}
              <div className="form-section">
                <h3>Ek Ayarlar</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Kullanım Limiti (boş = sınırsız)</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Sınırsız"
                      value={formData.usage_limit || ''}
                      onChange={(e) => setFormData({...formData, usage_limit: e.target.value ? parseInt(e.target.value) : null})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    />
                    <span>Kampanya aktif olsun</span>
                  </label>
                </div>
                <div className="form-group" style={{ marginTop: '8px' }}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.ecommerce_active}
                      onChange={(e) => setFormData({...formData, ecommerce_active: e.target.checked})}
                    />
                    <ShoppingCart size={16} style={{ marginRight: '4px', color: '#8b5cf6' }} />
                    <span>E-ticarete aç (Shopify İndirimler sayfasına gönder)</span>
                  </label>
                  {formData.ecommerce_active && (
                    <div style={{
                      marginTop: '6px',
                      padding: '8px 12px',
                      background: '#f5f3ff',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#6d28d9',
                      border: '1px solid #ddd6fe'
                    }}>
                      <ShoppingCart size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      Bu kampanya kaydedildiğinde Shopify İndirimler sayfasına otomatik olarak gönderilecektir.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>İptal</button>
              <button className="btn-save" onClick={handleSubmit}>
                {editingCampaign ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yardım Modal */}
      {showHelpModal && (
        <div className={`modal-overlay ${closingModal === 'help' ? 'closing' : ''}`}>
          <div className="modal help-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header help-header">
              <h2><BookOpen size={20} style={{ marginRight: '8px' }} /> Kampanyalar Yardım</h2>
              <button className="close-btn" onClick={() => handleCloseModal('help', setShowHelpModal)}>X</button>
            </div>
            <div className="help-modal-content">
              {/* Genel Bilgi */}
              <div className="help-section">
                <h3><Gift size={18} style={{ marginRight: '6px' }} /> Genel Bilgi</h3>
                <p>Bu sayfa üzerinden müşterilerinize özel kampanyalar ve bakiyeler oluşturabilirsiniz. Farklı kampanya tipleri ile esnek indirim seçenekleri sunabilirsiniz.</p>
              </div>

              {/* Kampanya Tipleri */}
              <div className="help-section">
                <h3><Tag size={18} style={{ marginRight: '6px' }} /> Kampanya Tipleri</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">%</span>
                    <span><strong>Yüzde İndirim:</strong> Toplam tutardan yüzde indirim uygular</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">TL</span>
                    <span><strong>Tutar İndirim:</strong> Sabit tutar indirim uygular</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">1-2-3</span>
                    <span><strong>Kademeli İndirim:</strong> 1. ürün %10, 2. ürün %20 gibi artan indirimler</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3=2</span>
                    <span><strong>X Al Y Öde:</strong> 3 al 2 öde gibi kampanyalar</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">SET</span>
                    <span><strong>Set Kampanyası:</strong> Belirli ürünler birlikte alındığında indirim</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">HEDİYE</span>
                    <span><strong>Hediye Kampanyası:</strong> Belirli alışverişlerde ücretsiz ürün</span>
                  </div>
                </div>
              </div>

              {/* Yeni Kampanya */}
              <div className="help-section">
                <h3><Plus size={18} style={{ marginRight: '6px' }} /> Yeni Kampanya Oluşturma</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>+ Yeni Kampanya</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Kampanya adını ve açıklamasını girin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Kampanya tipini seçin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span>Koşulları belirleyin (minimum tutar, kategoriler vs.)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">5</span>
                    <span>İndirim oranını veya tutarını girin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">6</span>
                    <span>Başlangıç ve bitiş tarihlerini seçin</span>
                  </div>
                </div>
              </div>

              {/* Hediye Kartları */}
              <div className="help-section">
                <h3><Gift size={18} style={{ marginRight: '6px' }} /> Hediye Kartları</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>Hediye Kartları</strong> sekmesine geçin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span><strong>+ Yeni Hediye Kartı</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Kart kodunu girin veya otomatik oluşturun</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span>Kart tutarını ve geçerlilik tarihini belirleyin</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Hediye kartları satış sırasında ödeme yöntemlerinden kullanılabilir.</div>
              </div>

              {/* Mağaza Tipi */}
              <div className="help-section">
                <h3><Store size={18} style={{ marginRight: '6px' }} /> Mağaza Tipi Ayrımı</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num" style={{background: '#d97706', color: 'white'}}>A</span>
                    <span><strong>Altın Mağazalar:</strong> Sadece altın mağazalarında geçerli kampanyalar</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num" style={{background: '#64748b', color: 'white'}}>G</span>
                    <span><strong>Gümüş Mağazalar:</strong> Sadece gümüş mağazalarında geçerli kampanyalar</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num" style={{background: '#6366f1', color: 'white'}}>T</span>
                    <span><strong>Tüm Mağazalar:</strong> Tüm mağazalarda geçerli kampanyalar</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Admin kullanıcılar üst kısımdaki filtrelerden mağaza tipine göre kampanyaları görüntüleyebilir.</div>
              </div>

              {/* Durum Açıklamaları */}
              <div className="help-section">
                <h3><CheckCircle size={18} style={{ marginRight: '6px' }} /> Kampanya Durumları</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num" style={{background: '#22c55e', color: 'white'}}>A</span>
                    <span><strong>Aktif:</strong> Kampanya şu an geçerli ve kullanılabilir</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num" style={{background: '#f59e0b', color: 'white'}}>B</span>
                    <span><strong>Beklemede:</strong> Kampanya henüz başlamadı</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num" style={{background: '#ef4444', color: 'white'}}>P</span>
                    <span><strong>Pasif:</strong> Kampanya süresi doldu veya devre dışı</span>
                  </div>
                </div>
              </div>

              {/* Butonlar */}
              <div className="help-section help-section-buttons">
                <h3><BarChart3 size={18} style={{ marginRight: '6px' }} /> Buton Açıklamaları</h3>
                <div className="help-buttons-grid">
                  <div className="help-button-item">
                    <span className="btn-icon"><Plus size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Yeni Kampanya</strong>
                      <p>Yeni kampanya oluşturur</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ScrollText size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>İşlem Geçmişi</strong>
                      <p>Tüm işlem loglarını gösterir</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Pencil size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Düzenle</strong>
                      <p>Kampanya bilgilerini günceller</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Clock size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Kullanım İstatistikleri</strong>
                      <p>Kampanyanın ne kadar kullanıldığını gösterir</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* İpuçları */}
              <div className="help-section tips-section">
                <h3><Lightbulb size={18} style={{ marginRight: '6px' }} /> İpuçları</h3>
                <ul className="help-tips-list">
                  <li>Kampanyaları belirli kategorilere veya ürünlere sınırlandırabilirsiniz</li>
                  <li>Minimum alışveriş tutarı belirleyerek kampanya koşullarını oluşturun</li>
                  <li>Özel gün kampanyaları ile bayram ve tatil günlerinde indirim yapın</li>
                  <li>Doğum günü kampanyası ile müşterilerinizi mutlu edin</li>
                  <li>Kampanya kullanım istatistiklerini düzenli takip edin</li>
                  <li>Geçmiş kampanyaları referans alarak yeni kampanyalar oluşturun</li>
                  <li>Altın ve gümüş mağazalarına özel kampanyalar oluşturarak hedefli indirimler yapın</li>
                  <li>Mağaza yetkilileri sadece kendi mağaza tiplerindeki kampanyaları görebilir</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Log Modal */}
      <LogModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        module="campaigns"
        title="Kampanyalar"
        showEntityCode={false}
      />

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px', background: '#fff' }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#374151', lineHeight: '1.6' }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmModalConfig.onConfirm();
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                             confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                             'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
